// Everything that flies and everything that bursts. Rounds in the air — the
// bullets, the shells, the bombs, fish, flares, rockets and missiles — are
// drawn as light and smoke (streaks.js): tracers burn, shells glow and hang
// vapour, rockets and missiles trail motor smoke that drifts on the wind.
// What planes leave behind — contrails, streamers, the ace's gold — hangs
// in the same ribbons. What they end in is drawn from the Blender flipbooks
// (flipbooks.js): a fireball cooling to black smoke, a water column falling
// back as spray, dust rolling off a near miss, a broadside's flash and its
// brown cordite cloud. Sparks fly as streaks of light. Only what is actually
// solid — clods of earth, scraps of airframe — is still thrown as the
// world's blocks, and the block recipes stand in whole until the flipbooks
// land (or under ?boxes).

import * as THREE from "three";

import { SoAPool } from "./soapool.js";
import { touchInstances, uploadInstances } from "./instance_upload.js";
import { bakedGeometry, loadModel, modelsEnabled, paintMaterialDSInstanced } from "./models.js";
import { FX_OUT_GLSL, FX_UNIFORMS, StreakBatch, TrailRibbons, TRAIL_KIND, noiseTexture, setStreakVeil, shellGeometry } from "./streaks.js";
import { FX_SHEETS } from "./fx_sheets.js";

// A tracer is burning pyrotechnic, not paint: a white-hot spark in an orange
// halo, the same for everyone (no team tint — you read ownership from where
// the fire comes from). Heavy AA burns redder.
const TRACER_HALO = new THREE.Color(0xff9a3c);
const TRACER_FLAK = new THREE.Color(0xff6a24);
// The vapour every round drags: the faint streak its passage leaves in the air.
const VAPOR = new THREE.Color(0xe9edf2);
// How wide a tracer's halo burns against the caliber's tracer width.
const TRACER_HALO_K = 0.9;
// A round first shows up in a snapshot a tick or two after it left the gun:
// count it this old already, so its vapour starts just short of where it
// was first seen rather than at it.
const FIRST_SEEN_AGE = 0.02;
// A round the pool can't place (no clock to match by — the screenshot
// director, a replay's first frame) is simply old: full-length streaks.
const OLD_AGE = 10;
// Snapshot-to-snapshot matching for bullets, on the server's own clock: a
// round turns up within centimetres of where its last row said it would
// be, so this is slack for the wire's rounding, and far less than the
// half-metre between two barrels of a wing battery.
const BULLET_MATCH_M = 0.35;
// And going the same way (m/s): a round only gains gravity between ticks.
const BULLET_MATCH_V = 4;
// A round of an airburst caliber that vanishes with this much of its life
// still ahead of it (beyond the snapshot gap) didn't burst — it hit
// something. The slack covers a round first seen a tick after it was fired.
const AIRBURST_SLACK = 0.15;
// The gun splash's size against the caliber: its tracer width is already the
// caliber's size on screen (.303 0.26 m, .50 0.34, 20 mm 0.45, 40 mm 0.7), so
// the spout grows with it — a rifle round flicks up a white thread, a 20 mm
// a spout you can walk onto a deck, a Bofors a column.
const SPLASH_PER_TRACER_M = 13;
// Past this far from the eye a spout swells with the distance (up to
// SPLASH_FAR_GROW times), the way a far tracer holds its spark's size: from a
// chase camera the stream comes down 400-600 m ahead, where a .50's true
// spout is three pixels and says nothing about where the rounds went.
const SPLASH_TRUE_M = 250;
const SPLASH_FAR_GROW = 2.5;

/**
 * A round of this caliber into the water, `dist` metres from the eye: the
 * spout's width in metres.
 */
export function gunSplashSize(cal, dist = 0) {
  const grow = Math.min(SPLASH_FAR_GROW, Math.max(1, dist / SPLASH_TRUE_M));
  return SPLASH_PER_TRACER_M * (cal?.tracerWidthM ?? 0.34) * grow;
}

/**
 * Bullet rendering in the original prototype's spirit: most rounds are
 * invisible and only drag a faint vapour streak; every Nth round (per the
 * caliber table) is a tracer, a burning spark whose streak length and
 * width come from the caliber. AA guns always fire tracers.
 */
export class Tracers {
  constructor(scene, calibers = [], capacity = 512) {
    this.capacity = capacity;
    this.calibers = calibers;
    // The tracers themselves: light, not geometry. A spark held at the
    // minimum size dims instead of fattening, so a far stream of fire reads
    // as sparks rather than bars.
    this.glow = new StreakBatch(scene, capacity, { mode: "glow", minPx: 1.5, fadePow: 0.35 });
    // The vapour behind every round: thin, soft, lit by the sky, and gone to
    // nothing well before it could alias into a dotted line.
    this.vapor = new StreakBatch(scene, capacity, { mode: "soft", minPx: 0.55, fadePow: 1 });
    // Every round in the air, rebuilt wholesale from each 20 Hz snapshot. As
    // objects that was two fresh Vector3s per bullet per snapshot — twenty
    // thousand a second with the sky full of lead, all of it dead before the
    // next frame. `born` is when this client first saw the round (the effects
    // clock), which is what keeps a fresh round's vapour from reaching back
    // past the gun that fired it.
    this.bullets = new SoAPool(capacity, {
      f32: ["px", "py", "pz", "vx", "vy", "vz", "born"],
      u8: ["team", "kind", "cal", "tracer", "wet"],
    });
    // Last snapshot's rows as the server sent them (not as the client has
    // flown them since), and its server time: the next snapshot is matched
    // against these, carried forward on the server's clock.
    this.raw = new SoAPool(capacity, { f32: ["px", "py", "pz", "vx", "vy", "vz", "born"], u8: ["kind", "cal", "wet"] });
    this.rawNow = null;
    this.rawTime = 0; // the effects clock when they came in
    // (x, y, z) => where a round of an airburst caliber (the heavy flak's time
    // fuse) ran its whole life out: main.js draws the puff, if the spot is
    // open sky.
    this.onAirburst = null;
    // Where there's water (main.js sets these): the sea's surface (m), the
    // ground under a point, and (x, y, z, caliber) => the spout a round
    // throws coming down on open water. The server buries a round at the
    // surface without a word, so the client sees it go in for itself — in
    // the frame it crosses, or, if the snapshot that drops it lands first,
    // where its last row was carrying it. A round that went in is `wet`:
    // drawn no more, and never splashed twice, even if a late row still
    // carries it. Unset, nothing is wet and every round flies as before.
    this.waterY = null;
    this.groundAt = null;
    this.onSplash = null;
    this._order = new Uint32Array(capacity);
    this._qx = new Float64Array(capacity);
    this._taken = new Uint8Array(capacity);
    this.time = 0;
  }

  /**
   * Replace the bullet set from a snapshot (rows: [x,y,z,vx,vy,vz,team,kind,
   * caliber,tracer]). `now` is the snapshot's server time (s); with it each
   * round is matched to the one it continues, so it keeps its age — without
   * it (staged rows), every round counts as long since fired.
   */
  setFromSnapshot(rows, now = null) {
    const b = this.bullets;
    b.clear();
    const { px, py, pz, vx, vy, vz } = b.f;
    const { team, kind, cal, tracer, wet } = b.b;
    const n = Math.min(rows.length, b.capacity);
    b.count = n;
    for (let i = 0; i < n; i++) {
      const r = rows[i];
      px[i] = r[0]; py[i] = r[1]; pz[i] = r[2];
      vx[i] = r[3]; vy[i] = r[4]; vz[i] = r[5];
      team[i] = r[6] | 0;
      kind[i] = r[7] | 0;
      const ci = r[8] | 0;
      cal[i] = this.calibers[ci] ? ci : 0; // unknown caliber falls back, as before
      tracer[i] = r[9] ? 1 : 0;
      wet[i] = 0;
    }
    this._age(now);
  }

  /**
   * Stamp each new row with when the round was first seen: the stamp of the
   * row it continues, or now. The last snapshot's rows are carried forward
   * on the server's clock — straight, under gravity, as the server flies
   * them — so a round turns up within a hair of where it was expected;
   * rounds a wing's barrels apart, or a stream's spacing apart, can't be
   * mistaken for each other.
   */
  _age(now) {
    const b = this.bullets, o = this.raw;
    const n = b.count, on = o.count;
    const { px, py, pz, vx, vy, vz, born } = b.f;
    const dt = now != null && this.rawNow != null ? now - this.rawNow : -1;
    if (dt > 0 && dt < 0.6) {
      const { px: opx, py: opy, pz: opz, vx: ovx, vy: ovy, vz: ovz, born: oborn } = o.f;
      const wet = b.b.wet, owet = o.b.wet;
      // Old rows carried to `now`, ordered along x for a sweep.
      const qx = this._qx, order = this._order, taken = this._taken;
      for (let j = 0; j < on; j++) {
        qx[j] = opx[j] + ovx[j] * dt;
        order[j] = j;
        taken[j] = 0;
      }
      const ord = order.subarray(0, on);
      ord.sort((a, c) => qx[a] - qx[c]);
      const drop = 0.5 * 9.81 * dt * dt;
      const tol = BULLET_MATCH_M, tol2 = tol * tol;
      for (let i = 0; i < n; i++) {
        const x = px[i];
        // First candidate with qx >= x - tol.
        let lo = 0, hi = on;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (qx[ord[mid]] < x - tol) lo = mid + 1;
          else hi = mid;
        }
        let best = -1, bestD = tol2;
        for (let k = lo; k < on; k++) {
          const j = ord[k];
          if (qx[j] > x + tol) break;
          if (taken[j]) continue;
          const dx = x - qx[j];
          const dy = py[i] - (opy[j] + ovy[j] * dt - drop), dz = pz[i] - (opz[j] + ovz[j] * dt);
          const d2 = dx * dx + dy * dy + dz * dz;
          if (d2 >= bestD) continue;
          const dvx = vx[i] - ovx[j], dvy = vy[i] - ovy[j], dvz = vz[i] - ovz[j];
          if (dvx * dvx + dvy * dvy + dvz * dvz > BULLET_MATCH_V * BULLET_MATCH_V) continue;
          best = j;
          bestD = d2;
        }
        if (best >= 0) {
          taken[best] = 1;
          born[i] = oborn[best];
          wet[i] = owet[best];
        } else {
          born[i] = this.time - FIRST_SEEN_AGE;
        }
      }
      if (this.onAirburst) this._airbursts(dt);
      if (this.onSplash && this.waterY != null) this._lateSplashes(dt);
    } else {
      // No clock to match by: long since fired, as far as anyone can tell.
      for (let i = 0; i < n; i++) born[i] = this.time - OLD_AGE;
    }
    // Keep this snapshot as sent, to match the next one against.
    const r = this.raw.f;
    r.px.set(px.subarray(0, n)); r.py.set(py.subarray(0, n)); r.pz.set(pz.subarray(0, n));
    r.vx.set(vx.subarray(0, n)); r.vy.set(vy.subarray(0, n)); r.vz.set(vz.subarray(0, n));
    r.born.set(born.subarray(0, n));
    this.raw.b.kind.set(b.b.kind.subarray(0, n));
    this.raw.b.cal.set(b.b.cal.subarray(0, n));
    this.raw.b.wet.set(b.b.wet.subarray(0, n));
    this.raw.count = n;
    this.rawNow = now;
    this.rawTime = this.time;
  }

  /**
   * The rounds nobody continued this snapshot are gone. A flak round of an
   * airburst caliber that had run (all but) its whole life out went off where
   * its fuse did; one cut short hit something, or flew out of this client's
   * sight, and gets no puff. `dt` is the server time since the last rows.
   */
  _airbursts(dt) {
    const o = this.raw;
    const { px, py, pz, vx, vy, vz, born } = o.f;
    const kind = o.b.kind, cal = o.b.cal, taken = this._taken;
    for (let j = 0; j < o.count; j++) {
      if (taken[j] || kind[j] !== 1) continue;
      const c = this.calibers[cal[j]];
      if (!c?.airburst) continue;
      // How long it had left at its last row, by this client's reckoning (a
      // round first seen late reads younger: no puff, rather than a false one).
      const left = (c.bulletLifeSec ?? 0) - (this.rawTime - born[j]);
      if (left > dt + AIRBURST_SLACK) continue;
      const t = Math.min(dt, Math.max(0, left));
      this.onAirburst(px[j] + vx[j] * t, py[j] + vy[j] * t - 4.905 * t * t, pz[j] + vz[j] * t);
    }
  }

  /**
   * The rounds nobody continued that went into the sea since their last row:
   * the frame that would have seen them cross never came before the snapshot
   * that dropped them. Splash each where its own flight met the surface — if
   * it met it before the round's life ran out (one that expired in the air
   * just short of the water never got there).
   */
  _lateSplashes(dt) {
    const o = this.raw, wy = this.waterY;
    const { px, py, pz, vx, vy, vz, born } = o.f;
    const cal = o.b.cal, wet = o.b.wet, taken = this._taken;
    for (let j = 0; j < o.count; j++) {
      if (taken[j] || wet[j] || py[j] <= wy) continue;
      // The first time its arc reaches the surface: py + vy t - g/2 t² = wy.
      const v = vy[j];
      const t = (v + Math.sqrt(v * v + 2 * 9.81 * (py[j] - wy))) / 9.81;
      if (t > dt + AIRBURST_SLACK) continue; // gone in the air: hit something
      if (this._spent(cal[j], this.rawTime - born[j] + t)) continue;
      this._splash(px[j] + vx[j] * t, pz[j] + vz[j] * t, cal[j]);
    }
  }

  // Had a round of caliber `ci` this old already run its life out? Its age is
  // only known for a round this client has watched fly (and reads a tick
  // young at that); a staged one's isn't, and it's given the benefit.
  _spent(ci, age) {
    return age < OLD_AGE && age > (this.calibers[ci]?.bulletLifeSec ?? Infinity);
  }

  // A round reached the surface at (x, z): over open water it throws a spout;
  // over a shore the ground took it first.
  _splash(x, z, ci) {
    const wy = this.waterY;
    if (this.groundAt && this.groundAt(x, z) >= wy) return;
    this.onSplash(x, wy, z, this.calibers[ci] ?? {});
  }

  /** Advance every round by dt and draw it; `eye` sorts the streaks. */
  update(dt, eye = null) {
    const b = this.bullets;
    const { px: pxA, py: pyA, pz: pzA, vx: vxA, vy: vyA, vz: vzA, born: bornA } = b.f;
    const kindA = b.b.kind, tracerA = b.b.tracer, calA = b.b.cal, wetA = b.b.wet;
    const n = b.count;
    const wy = this.waterY ?? -Infinity;
    // The last snapshot's rows sit at the same indices as these rounds (one
    // copy of the other): a round that goes in is marked on both, so the next
    // snapshot's row for it — if the server hadn't buried it yet — stays wet.
    const rawWet = this.raw.count === n ? this.raw.b.wet : null;
    const glow = this.glow, vapor = this.vapor;
    this.time += dt;
    const now = this.time;
    glow.begin();
    vapor.begin();
    for (let i = 0; i < n; i++) {
      // Advance between snapshots.
      const vx = vxA[i], vy = vyA[i], vz = vzA[i];
      const px = (pxA[i] += vx * dt);
      const py = (pyA[i] += vy * dt);
      const pz = (pzA[i] += vz * dt);
      if (wetA[i]) continue;
      if (py < wy) {
        // Under the surface: into the sea this frame, or a row that turned
        // up already buried. Either way it's done flying.
        wetA[i] = 1;
        if (rawWet) rawWet[i] = 1;
        const was = py - vy * dt;
        if (was >= wy && this.onSplash && !this._spent(calA[i], now - bornA[i])) {
          const s = (was - wy) / (was - py); // how far through the frame it crossed
          this._splash(px - vx * dt * (1 - s), pz - vz * dt * (1 - s), calA[i]);
        }
        continue;
      }
      const speed = Math.sqrt(vx * vx + vy * vy + vz * vz);
      if (speed < 1) continue;
      const inv = 1 / speed;
      const cal = this.calibers[calA[i]] ?? {};
      const tr = tracerA[i];
      // How long it has been flying, as far as this client knows: nothing it
      // drags may reach back past the gun it left.
      const age = now - bornA[i];

      // Vapour: a thin streak from the round back along its path, thickest
      // right behind it, spreading and thinning away to nothing. A tracer's
      // burning compound smokes, so its streak is a shade denser; flak barely
      // leaves one at all — what reads of an AA stream is its burning
      // tracers, not white rods hung across the sky.
      const trailSec = Math.min(cal.trailSec ?? 0.12, age);
      if (trailSec <= 0) continue;
      const tw = (cal.trailWidthM ?? 0.2) * 0.5;
      const k = -trailSec;
      vapor.push(
        px, py, pz, tw * 0.55,
        px + vx * k, py + vy * k, pz + vz * k, tw * 1.6,
        VAPOR.r, VAPOR.g, VAPOR.b, kindA[i] === 1 ? 0.1 : tr ? 0.26 : 0.2,
      );

      // Tracer rounds: the burning spark, stretched along its flight the way
      // the eye (and every gun camera) smears it. The rest stay invisible.
      if (!tr) continue;
      const len = Math.min(cal.tracerLengthM ?? 6, speed * age + 0.5);
      const w = (cal.tracerWidthM ?? 0.4) * 0.5 * TRACER_HALO_K;
      const c = kindA[i] === 1 ? TRACER_FLAK : TRACER_HALO;
      // Pyrotechnic burn is never steady: a little shimmer round to round.
      const flick = 0.82 + Math.random() * 0.3;
      glow.push(
        px, py, pz, w,
        px - vx * inv * len, py - vy * inv * len, pz - vz * inv * len, w * 0.55,
        c.r, c.g, c.b, flick,
      );
    }
    vapor.end(eye);
    glow.end(eye);
  }
}

// What a round in the Bombs pool actually is. Everything that falls, runs, or
// burns shares one section of the wire and one pool here, so the type rides
// along as bits: a plain gravity bomb is simply none of them.
export const BOMB_SHELL = 1;
export const BOMB_TORP = 2;
export const BOMB_FLARE = 4;
export const BOMB_MSL = 8;
export const BOMB_ROCKET = 16;
// A super-heavy main-battery shell (the wire's heavy bit): the Yamato's
// 18.1-inch, drawn at its size whoever's watching.
export const BOMB_HEAVY = 32;

// A round's size class, fixed the first time it's seen: a shell is a
// battleship's (0), a tank's (1) or a super-heavy's (2, the Yamato's
// 18.1-inch, flagged on the wire); a flare a star shell (0) or a jet's
// decoy (1).
export const SHELL_NAVAL = 0;
export const SHELL_TANK = 1;
export const SHELL_HEAVY = 2;
// The super-heavy at its true size against the 16-inch shell mesh (460 mm
// over 406), its tracer burning longer and wider in proportion: the round a
// captain can pick out of a mixed salvo by eye.
const HEAVY_SCALE = 460 / 406;
const FLARE_STAR = 0;
const FLARE_DECOY = 1;
// Faster than this at first sight, a flare was kicked out of a jet, not
// hung under a star shell's chute (those sink at ~9 m/s).
const DECOY_MIN_SPEED = 25;

// Glow colours. A shell's tracer is a hot orange ember at its base; a motor
// is a yellow-white jet going orange at its end; a star shell burns
// magnesium white in a warm haze.
const SHELL_GLOW = new THREE.Color(0xffa860);
const MOTOR_GLOW = new THREE.Color(0xffa040);
const FLARE_CORE = new THREE.Color(0xfff2d6);
const FLARE_HALO = new THREE.Color(0xffd98c);
const WAKE = new THREE.Color(0xf2f8fb);

// Where the motor sits behind each model's middle (model-integration.md 1c-ter:
// the HVAR's exhaust is at x −0.9, the missile's at x −2.3).
const ROCKET_EXHAUST_M = 0.95;
const MISSILE_EXHAUST_M = 2.35;

// Snapshot-to-snapshot matching (see _match): where a round should be now is
// carried from where its last row put it, on the snapshots' own clock, so it
// turns up within a few metres of the prediction along its heading and a
// sliver across it — more across for a missile pulling a hard turn.
const MATCH_ALONG_FRAC = 0.25; // of the distance flown since the last row
const MATCH_ALONG_M = 10;
const MATCH_CROSS_M = 2;
const MATCH_CROSS_PER_M = 0.05;
const MATCH_TURN_G = 200; // m/s²: the sharpest turn a guided round is allowed between rows
// Gravity each kind feels between rows, as the server flies it (a running
// fish, a missile and a flare hold their line).
const gravityOf = (kind) => (kind & (BOMB_MSL | BOMB_FLARE) ? 0 : kind & BOMB_ROCKET ? 9.81 * 0.3 : 9.81);

/**
 * Everything that falls, runs, or burns: gravity bombs, main-battery and tank
 * shells, torpedoes, star shells and decoy flares, wire-guided and heat-
 * seeking missiles, and aircraft rockets — one wire section, one pool.
 *
 * Bombs, fish, rockets and missiles fly as their models. A shell is a turned
 * steel round with a glowing tracer at its base; a motor burns as a flame; a
 * flare as a white-hot star in its own haze. What they leave behind hangs in
 * the air as ribbons (streaks.js): the chalk line of a shell's arc, the
 * billowing rope of a rocket or a missile, the plume climbing off a flare.
 *
 * The wire carries no identity for a round, and a trail needs one — the
 * smoke laid a second ago belongs to the missile that's still flying. So
 * each snapshot is matched against the rounds the pool was already
 * carrying (the client flies them between snapshots, so a round is found
 * almost exactly where it was expected; see _match), and a round keeps its
 * id, and its trail, for as long as it flies.
 */
export class Bombs {
  constructor(scene, capacity = 128, starLightRadius = 1700, { shellClass = null } = {}) {
    this.capacity = capacity;
    this.starLightRadius = starLightRadius;
    // shellClass(x, y, z) -> SHELL_NAVAL | SHELL_TANK for a shell first seen
    // there (the theater decides; combined arms asks where it was fired from).
    this.shellClass = shellClass;

    // Stand-in boxes for a round whose model hasn't loaded (or ?boxes).
    this.mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.7, 0.7, 2.2),
      new THREE.MeshLambertMaterial({ color: 0x23262c }),
      capacity,
    );
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    scene.add(this.mesh);

    // The shells: turned steel, a battleship's at its true 16-inch size and a
    // tank's scaled down to its gun.
    this.shellMesh = new THREE.InstancedMesh(
      shellGeometry(0.41, 1.85),
      new THREE.MeshLambertMaterial({ color: 0x5a5d62 }),
      capacity,
    );
    this.shellMesh.frustumCulled = false;
    this.shellMesh.count = 0;
    scene.add(this.shellMesh);

    // The light: shell tracers, motor flames, star shells.
    this.glow = new StreakBatch(scene, capacity * 2, { mode: "glow", minPx: 1.3, fadePow: 0.45 });
    // Torpedo tracks: a foaming bubble trail on the surface behind each fish.
    this.wake = new StreakBatch(scene, 32, { mode: "flat" });
    // The smoke and vapour they leave hanging.
    this.trails = new TrailRibbons(scene);

    // A few real lights follow the nearest live star shells so hulls under
    // them glow.
    this.flareLights = [];
    for (let i = 0; i < 4; i++) {
      // decay 0: an even pool of light out to the radius (a flare hangs ~400 m
      // up, so physical inverse-square falloff would never reach the sea).
      const L = new THREE.PointLight(0xffe6a8, 0, this.starLightRadius, 0);
      L.castShadow = false;
      scene.add(L);
      this.flareLights.push(L);
    }

    // The ordnance models (Part 6c): the 500 lb bomb, the Mk 13 fish, the HVAR
    // and the missile, each its own instanced mesh once it loads (anything
    // still loading keeps the box). The art points noses along +X; the pool
    // orients rounds with lookAt, which points +Z — hence the turn.
    this.models = {}; // BOMB_* bit (0 = gravity bomb) -> InstancedMesh
    if (modelsEnabled()) {
      const noseToZ = new THREE.Matrix4().makeRotationY(-Math.PI / 2);
      const kinds = [[0, "bomb_500lb"], [BOMB_TORP, "torpedo_mk13"], [BOMB_ROCKET, "rocket_hvar"], [BOMB_MSL, "missile"]];
      for (const [bit, stem] of kinds) {
        loadModel(stem).then((t) => {
          const { geometry } = bakedGeometry(t, { matrix: noseToZ });
          const m = new THREE.InstancedMesh(geometry, paintMaterialDSInstanced, capacity);
          m.frustumCulled = false;
          m.count = 0;
          scene.add(m);
          this.models[bit] = m;
        }, () => {});
      }
    }

    this.dummy = new THREE.Object3D();
    // Bombs, shells, fish, flares, missiles and rockets all ride one pool.
    // `kind` is a bitfield rather than six booleans; BOMB_* names it. `id`
    // is the round's identity across snapshots, `cls` its size class.
    const spec = {
      f32: ["px", "py", "pz", "vx", "vy", "vz"],
      u32: ["id"],
      u8: ["team", "kind", "cls"],
    };
    this.bombs = new SoAPool(capacity, spec);
    // Last snapshot's rows as sent (not as the client has flown them since),
    // and the moment they were for: the next set is matched against these.
    this.prev = new SoAPool(capacity, spec);
    this.prevNow = null;
    this.nextId = 1;
    this._taken = new Uint8Array(capacity);
    this._pairs = { cost: new Float64Array(0), order: new Uint32Array(0) };
    this.time = 0;
  }

  /** Replace the bomb set from a snapshot
   * (rows: [x,y,z,vx,vy,vz,team,shell,torpedo,flare,missile,rocket,chute,heavy]).
   * `now` is the snapshot's server time (s); rows staged without one (the
   * screenshot director) are timed by the wall clock instead. */
  setFromSnapshot(rows, now = null) {
    const t = now ?? (typeof performance !== "undefined" ? performance.now() / 1000 : 0);
    const dt = this.prevNow == null ? 0 : t - this.prevNow;
    this.prevNow = t;
    const b = this.bombs;
    b.clear();
    const { px, py, pz, vx, vy, vz } = b.f;
    const teamA = b.b.team, kindA = b.b.kind;
    let n = 0;
    for (const r of rows) {
      if (r[12]) continue; // chute bit: parachutes ride this section but Chutes draws them
      if (n >= b.capacity) break;
      px[n] = r[0]; py[n] = r[1]; pz[n] = r[2];
      vx[n] = r[3]; vy[n] = r[4]; vz[n] = r[5];
      teamA[n] = r[6] | 0;
      kindA[n] =
        (r[7] ? BOMB_SHELL : 0) |
        (r[8] ? BOMB_TORP : 0) |
        (r[9] ? BOMB_FLARE : 0) |
        // land wire-guided missile: flies level, drags a motor glow
        (r[10] ? BOMB_MSL : 0) |
        (r[11] ? BOMB_ROCKET : 0) |
        (r[13] ? BOMB_HEAVY : 0);
      n++;
    }
    b.count = n;
    this._match(dt > 0 && dt < 2 ? dt : 0);
    // Keep this set as sent, to match the next one against (ids and classes
    // included; the client flies its own copy in `bombs`).
    const o = this.prev;
    for (const k of ["px", "py", "pz", "vx", "vy", "vz"]) o.f[k].set(b.f[k].subarray(0, n));
    o.u.id.set(b.u.id.subarray(0, n));
    for (const k of ["team", "kind", "cls"]) o.b[k].set(b.b[k].subarray(0, n));
    o.count = n;
  }

  /**
   * Give every new row the identity of the round it continues, or a new one.
   * Each of the last set's rows is carried `dt` seconds forward, as the
   * server flies it, so a round turns up a few metres from where it was
   * expected along its heading and next to nothing across it — whatever the
   * client's frame rate did in between (a client drawing a frame a second
   * flies its own copy only a tenth of a second a frame). Two shells out of
   * neighbouring barrels fly a couple of metres apart and a rocket stick
   * strings its rounds down one line, so the pairs are settled cheapest
   * first across the whole set, not row by row: a round can't be claimed by
   * the one behind it just because that one was read first.
   */
  _match(dt) {
    const b = this.bombs, o = this.prev;
    const n = b.count, on = o.count;
    const { px, py, pz } = b.f;
    const kindA = b.b.kind, teamA = b.b.team, idA = b.u.id, clsA = b.b.cls;
    const { px: rpx, py: rpy, pz: rpz, vx: ovx, vy: ovy, vz: ovz } = o.f;
    const okind = o.b.kind, oteam = o.b.team, oid = o.u.id, ocls = o.b.cls;
    // Where each old round should be now (a running fish holds its depth).
    const q = this._q ??= { x: new Float64Array(this.capacity), y: new Float64Array(this.capacity), z: new Float64Array(this.capacity) };
    for (let j = 0; j < on; j++) {
      const k = okind[j];
      const g = k & BOMB_TORP && Math.abs(ovy[j]) < 0.5 ? 0 : gravityOf(k);
      q.x[j] = rpx[j] + ovx[j] * dt;
      q.y[j] = rpy[j] + ovy[j] * dt - 0.5 * g * dt * dt;
      q.z[j] = rpz[j] + ovz[j] * dt;
    }
    const opx = q.x, opy = q.y, opz = q.z;
    const turnSlack = 0.5 * MATCH_TURN_G * dt * dt;
    // Candidate pairs within tolerance, costed by how far off the expected
    // line the new row sits (across counts far more than along).
    let pairs = this._pairs;
    if (pairs.cost.length < n * on) {
      const cap = Math.max(64, n * on);
      pairs = this._pairs = { cost: new Float64Array(cap), order: new Uint32Array(cap), i: new Uint16Array(cap), j: new Uint16Array(cap) };
    }
    let m = 0;
    for (let i = 0; i < n; i++) {
      idA[i] = 0;
      for (let j = 0; j < on; j++) {
        if (okind[j] !== kindA[i] || oteam[j] !== teamA[i]) continue;
        const dx = px[i] - opx[j], dy = py[i] - opy[j], dz = pz[i] - opz[j];
        const sp = Math.sqrt(ovx[j] * ovx[j] + ovy[j] * ovy[j] + ovz[j] * ovz[j]);
        const d2 = dx * dx + dy * dy + dz * dz;
        const along = sp > 0.5 ? (dx * ovx[j] + dy * ovy[j] + dz * ovz[j]) / sp : 0;
        if (Math.abs(along) > sp * dt * MATCH_ALONG_FRAC + MATCH_ALONG_M) continue;
        const cross2 = Math.max(0, d2 - along * along);
        const tol = MATCH_CROSS_M + MATCH_CROSS_PER_M * Math.abs(along) + (okind[j] & BOMB_MSL ? turnSlack : 0);
        if (cross2 > tol * tol) continue;
        pairs.cost[m] = cross2 + 0.01 * along * along;
        pairs.i[m] = i;
        pairs.j[m] = j;
        pairs.order[m] = m;
        m++;
      }
    }
    const order = pairs.order.subarray(0, m);
    const cost = pairs.cost;
    order.sort((a, c) => cost[a] - cost[c]);
    const taken = this._taken;
    taken.fill(0, 0, on);
    for (let k = 0; k < m; k++) {
      const pk = order[k];
      const i = pairs.i[pk], j = pairs.j[pk];
      if (idA[i] !== 0 || taken[j]) continue;
      idA[i] = oid[j];
      clsA[i] = ocls[j];
      taken[j] = 1;
    }
    // Everything unclaimed is new: a fresh id, and its size class read now,
    // where it was first seen.
    const { vx, vy, vz } = b.f;
    for (let i = 0; i < n; i++) {
      if (idA[i] !== 0) continue;
      idA[i] = this.nextId++;
      if (this.nextId > 0xfffffff0) this.nextId = 1;
      const kd = kindA[i];
      if (kd & BOMB_HEAVY) clsA[i] = SHELL_HEAVY; // the server said so: no guessing from where it showed
      else if (kd & BOMB_SHELL) clsA[i] = this.shellClass ? this.shellClass(px[i], py[i], pz[i]) : SHELL_NAVAL;
      else if (kd & BOMB_FLARE) clsA[i] = Math.hypot(vx[i], vy[i], vz[i]) > DECOY_MIN_SPEED ? FLARE_DECOY : FLARE_STAR;
      else clsA[i] = 0;
    }
    // The old rounds nobody continued are gone: their trails end where they
    // were last drawn and hang there, fading.
    for (let j = 0; j < on; j++) {
      if (!taken[j]) this.trails.end(oid[j]);
    }
  }

  /**
   * Advance every round by dt and draw it. `eye` (the camera's position)
   * sorts the light and smoke among the other see-through draws; `windX` /
   * `windZ` (m/s) carry the smoke downwind.
   */
  update(dt, { eye = null, windX = 0, windZ = 0 } = {}) {
    const b = this.bombs;
    const { px: pxA, py: pyA, pz: pzA, vx: vxA, vy: vyA, vz: vzA } = b.f;
    const kindA = b.b.kind, idA = b.u.id, clsA = b.b.cls;
    const n = b.count;
    const glow = this.glow, wake = this.wake, trails = this.trails;
    let meshN = 0;
    let shellN = 0;
    const modelN = {};
    this.time += dt;
    glow.begin();
    wake.begin();
    for (let i = 0; i < n; i++) {
      const kind = kindA[i];
      let vx = vxA[i], vy = vyA[i], vz = vzA[i];
      // Star shells and decoys drift at the server's rate (no gravity) and
      // burn as light: a white-hot star in a warm haze, flickering, with a
      // plume of smoke climbing off it.
      if (kind & BOMB_FLARE) {
        const px = (pxA[i] += vx * dt);
        const py = (pyA[i] += vy * dt);
        const pz = (pzA[i] += vz * dt);
        const decoy = clsA[i] === FLARE_DECOY;
        const ph = idA[i] * 2.3; // its own flicker, whatever slot it lands in
        const flick = 0.82 + 0.18 * Math.sin(this.time * 17 + ph) * Math.sin(this.time * 7.1 + ph * 0.43);
        // A star shell lights the sea for a kilometre and hangs in a wide
        // haze; a decoy is a pinpoint of burning magnesium, too small and
        // too brief for more than a tight glow round it.
        const core = decoy ? 0.9 : 3.6, halo = decoy ? 4.5 : 48;
        // A disc: one point, one radius (a head and tail of different sizes
        // at the same spot would seam the disc down the middle).
        const hr = halo * (0.94 + 0.06 * flick), cr = core * flick;
        glow.push(px, py, pz, hr, px, py, pz, hr, FLARE_HALO.r, FLARE_HALO.g, FLARE_HALO.b, (decoy ? 0.5 : 0.34) * flick);
        glow.push(px, py, pz, cr, px, py, pz, cr, FLARE_CORE.r, FLARE_CORE.g, FLARE_CORE.b, 1.7 * flick);
        trails.feed(idA[i], px, py, pz, vx, vy, vz, decoy ? TRAIL_KIND.decoySmoke : TRAIL_KIND.flareSmoke);
        continue;
      }
      // Running torpedoes hold their depth, wire-guided missiles fly their
      // guided line (the server steers them; between snapshots hold course),
      // rockets ride a motor-flattened arc (mirror the server's
      // rocketGravityFrac); everything else falls.
      const msl = kind & BOMB_MSL;
      const running = (kind & BOMB_TORP) && Math.abs(vy) < 0.5;
      if (!running && !msl) {
        vy -= 9.81 * (kind & BOMB_ROCKET ? 0.3 : 1) * dt;
        vyA[i] = vy;
      }
      const px = (pxA[i] += vx * dt);
      const py = (pyA[i] += vy * dt);
      const pz = (pzA[i] += vz * dt);
      const speed = Math.sqrt(vx * vx + vy * vy + vz * vz);
      const inv = speed > 1e-3 ? 1 / speed : 0;
      const fx = vx * inv, fy = vy * inv, fz = vz * inv; // heading, unit
      this.dummy.position.set(px, py, pz);
      this.dummy.lookAt(px + vx, py + vy, pz + vz);

      if (kind & BOMB_SHELL) {
        // A turned steel round, a tracer burning at its base, and the chalk
        // line of vapour it hangs along its arc.
        const tank = clsA[i] === SHELL_TANK;
        const heavy = clsA[i] === SHELL_HEAVY;
        const s = tank ? 0.3 : heavy ? HEAVY_SCALE : 1;
        this.dummy.scale.set(s, s, s);
        this.dummy.updateMatrix();
        this.shellMesh.setMatrixAt(shellN++, this.dummy.matrix);
        if (speed > 1) {
          const len = tank ? Math.min(Math.max(speed * 0.016, 8), 24) : Math.min(Math.max(speed * 0.028, 10), 26) * s;
          const r = tank ? 0.36 : 0.9 * s;
          const hx = px - fx * 0.9 * s, hy = py - fy * 0.9 * s, hz = pz - fz * 0.9 * s;
          glow.push(hx, hy, hz, r, hx - fx * len, hy - fy * len, hz - fz * len, r * 0.45, SHELL_GLOW.r, SHELL_GLOW.g, SHELL_GLOW.b, tank ? 0.95 : 1.05);
          trails.feed(idA[i], px, py, pz, vx, vy, vz, tank ? TRAIL_KIND.tankVapor : TRAIL_KIND.shellVapor);
        }
        continue;
      }

      // Bombs, fish, rockets and missiles fly as their models (a box until
      // one loads).
      const bit = kind & BOMB_TORP ? BOMB_TORP : msl ? BOMB_MSL : kind & BOMB_ROCKET ? BOMB_ROCKET : 0;
      const model = this.models[bit];
      if (model) {
        this.dummy.scale.set(1, 1, 1);
        this.dummy.updateMatrix();
        modelN[bit] = (modelN[bit] ?? 0) + 1;
        model.setMatrixAt(modelN[bit] - 1, this.dummy.matrix);
      } else {
        const s = kind & BOMB_TORP ? 2.6 : msl ? 1.7 : kind & BOMB_ROCKET ? 0.8 : 1;
        this.dummy.scale.set(s, s, s);
        this.dummy.updateMatrix();
        this.mesh.setMatrixAt(meshN++, this.dummy.matrix);
      }

      if ((msl || kind & BOMB_ROCKET) && speed > 1) {
        // The motor: a yellow-white jet off the nozzle, going orange where it
        // thins, never quite the same length twice — and the smoke it lays.
        const back = msl ? MISSILE_EXHAUST_M : ROCKET_EXHAUST_M;
        const flick = 0.8 + Math.random() * 0.4;
        const len = (msl ? 5.5 : 3.6) * flick;
        const r = msl ? 0.5 : 0.34;
        const hx = px - fx * back, hy = py - fy * back, hz = pz - fz * back;
        glow.push(hx, hy, hz, r, hx - fx * len, hy - fy * len, hz - fz * len, r * 0.3, MOTOR_GLOW.r, MOTOR_GLOW.g, MOTOR_GLOW.b, 1.35 * flick);
        trails.feed(idA[i], hx, hy, hz, vx, vy, vz, msl ? TRAIL_KIND.missileSmoke : TRAIL_KIND.rocketSmoke);
      }
      if (running && speed > 0.5) {
        // The bubble track: foam on the surface just above the fish, spreading
        // and breaking up astern.
        const len = Math.max(14, speed * 1.3);
        const y = py + 0.8;
        wake.push(px, y, pz, 1.1, px - fx * len, y, pz - fz * len, 4, WAKE.r, WAKE.g, WAKE.b, 0.9);
      }
    }
    uploadInstances(this.mesh, meshN);
    uploadInstances(this.shellMesh, shellN);
    for (const [bit, m] of Object.entries(this.models)) uploadInstances(m, modelN[bit] ?? 0);
    glow.end(eye);
    wake.end(eye);
    trails.update(dt, windX, windZ, eye);

    // Point the light pool at the first few live star shells so the hulls
    // beneath them lift out of the dark; park the rest dark. (A jet's decoy
    // burns too briefly and too high to light anything.) Walked rather than
    // filtered — there are at most four lights and this runs every frame.
    let lit = 0;
    for (let i = 0; i < n && lit < this.flareLights.length; i++) {
      if (!(kindA[i] & BOMB_FLARE) || clsA[i] === FLARE_DECOY) continue;
      const L = this.flareLights[lit++];
      L.position.set(pxA[i], pyA[i], pzA[i]);
      L.intensity = 2.2;
    }
    for (let i = lit; i < this.flareLights.length; i++) this.flareLights[i].intensity = 0;
  }

  /** True if the round at slot `i` carries every bit in `mask` (a BOMB_*). */
  is(i, mask) {
    return (this.bombs.b.kind[i] & mask) !== 0;
  }
}

// Smoke screens (naval and land): a low grey bank over the water or the
// steppe that hides the hulls inside it. Fed [x, z, team, lifeFrac] rows from
// the snapshot (lifeFrac runs 1 -> 0 as the screen burns out).
//
// The bank is one body of smoke, not a heap of puffs. It used to be ninety-six
// flipbook cards, and from a bridge or a chase camera it read as a pile of
// cotton balls: every card's round rim showed, and each carried the sheet's
// darker underside. Now the bank is a low, flat-topped drift of gas lying on
// the water, drawn as one volume: every pixel looks through it, measures how
// much smoke its sightline crosses, and is covered that much. So the middle
// stands thick enough to swallow a destroyer, the wall frays to nothing at
// the rim, a sightline skimming the sea through the whole bank is blind, and
// there is no card edge anywhere to catch the eye. The billow noise the
// trails are cut from (streaks.js) rolls its top, and the sun lights the
// faces of the rolls that lean toward it. Screens of every team go out in one
// draw; a burst beyond a bank sinks into it (transmit), and so does a tracer
// stream or a smoke rope (the streaks' veil, fed the same drums), and an eye
// inside one is fogged in to the bank's own grey (squeezeFog).
//
// Until the flipbooks land (or under ?boxes) the bank is the old translucent
// blocks.
export class Smoke {
  constructor(scene, radius = 700, { sprites = null, lite = false, groundAt = null } = {}) {
    this.radius = radius;
    // The soft particles (flipbooks.js), and the A/B switch the probes flip.
    this.sprites = sprites;
    this.useSprites = true;
    // Where a screen's foot lies: the sea by default; the land theaters hand
    // in the ground, or a screen popped on a ridge would sit buried at sea
    // level under it.
    this.groundAt = groundAt;
    this.puffN = 96;
    this.cap = this.puffN * 8;
    this.mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshLambertMaterial({ color: 0xb9c2cb, transparent: true, opacity: 0.34, depthWrite: false }),
      this.cap,
    );
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    scene.add(this.mesh);
    this.dummy = new THREE.Object3D();
    this.rows = [];
    this.waterY = 0;
    this.time = 0;
    this.deep = 0; // how deep in a bank the eye sat this frame (squeezeFog)
    // Deterministic golden-angle puff layout across the disc (the blocks).
    // Each puff carries its own size, a lift fraction, and a yaw so the
    // bank's top is lumpy, not a slab.
    this.puffs = [];
    for (let i = 0; i < this.puffN; i++) {
      const a = i * 2.39996;
      const rr = Math.sqrt(i / this.puffN);
      const sc = 0.6 + (i % 5) * 0.1;
      const lift = 0.4 + 0.6 * Math.abs(Math.sin(i * 1.7)) * (1 - rr); // taller in the middle
      this.puffs.push([Math.cos(a) * rr, Math.sin(a) * rr, sc, lift, a]);
    }
    this.body = bankBody(lite);
    scene.add(this.body);
    // This frame's banks, each [x, footY, z, thickness], for the veil and
    // the fog.
    this.banks = [];
    this._bankRows = [];
    // Every card drawn behind or inside a bank sinks into it (flipbooks.js
    // draws the cards after the body, so a splash beyond the screen would
    // otherwise stand out bright on its face).
    if (sprites) sprites.veil = (eye, x, y, z) => this.transmit(eye, x, y, z);
  }
  setFromSnapshot(rows, waterY) {
    this.rows = rows || [];
    this.waterY = waterY ?? this.waterY;
  }
  update(dt = 0, eye = null) {
    this.time += dt;
    const soft = this.useSprites && this.sprites?.ready;
    const R = this.radius;
    const H = bankHeight(R);
    const body = this.body.geometry;
    const at = body.attributes.iBank.array, life = body.attributes.iLife.array;
    let n = 0, banks = 0;
    this.banks.length = 0;
    for (const r of this.rows) {
      const x = r[0], z = r[1]; // [x, z, team, life]
      // A fresh screen thickens over its first second or so; a spent one
      // thins away over its last quarter instead of blinking out.
      const lf = r[3] ?? 1;
      const k = Math.min(1, lf / 0.25, (1 - lf) / 0.06 + 0.15);
      const y = this.groundAt ? Math.max(this.waterY, this.groundAt(x, z)) : this.waterY;
      if (soft) {
        // The [x, y, z, k] tuples are recycled frame to frame — the veil and
        // the fog squeeze only read them, and a screen-heavy round shouldn't
        // feed the collector a fresh array per bank per frame.
        const b = (this._bankRows[this.banks.length] ??= [0, 0, 0, 0]);
        b[0] = x; b[1] = y; b[2] = z; b[3] = k;
        this.banks.push(b);
        if (banks < BANK_MAX) {
          const o = banks * 4;
          at[o] = x; at[o + 1] = y; at[o + 2] = z; at[o + 3] = R;
          life[banks++] = k;
        }
        continue;
      }
      for (let i = 0; i < this.puffN; i++) {
        if (n >= this.cap) break;
        const [ox, oz, sc, lift, yaw] = this.puffs[i];
        const w = R * 0.26 * sc; // wide, heavily overlapping puffs
        const h = R * 0.11 * sc; // low — a bank on the water
        this.dummy.position.set(x + ox * R * 0.85, y + 5 + R * 0.05 * lift, z + oz * R * 0.85);
        this.dummy.scale.set(w, h, w);
        this.dummy.rotation.y = yaw;
        this.dummy.updateMatrix();
        this.mesh.setMatrixAt(n++, this.dummy.matrix);
      }
    }
    uploadInstances(this.mesh, n);
    body.instanceCount = banks;
    touchInstances(body.attributes.iBank, 0, banks);
    touchInstances(body.attributes.iLife, 0, banks);
    this.body.visible = banks > 0;
    const u = this.body.material.uniforms;
    u.uHeight.value = H;
    u.uTime.value = this.time;
    // The streaks and ribbons draw after the body too: they take the veil
    // through the drums transmit() measures, on the GPU. Under the blocks
    // there are no drums, and nothing is veiled (as for the cards).
    setStreakVeil(this.banks, R * VEIL_REACH, H * VEIL_TOP, (BANK_TAU / H) * VEIL_DENSITY, eye);
  }

  /**
   * Blind an eye sitting inside a bank: close the fog the sky just set (the
   * storm's recipe, storm.js) in to the bank's own sight range and turn it
   * the smoke's grey, by how deep in the bank the eye sits. The body can't
   * see a hull that stands inside it with the eye (the far face it's shaded
   * on lies behind the hull), but the fog grades every hull by its distance:
   * your own bow stays plain, a destroyer across the bank is gone. Returns
   * how deep the eye is, 0..1.
   */
  squeezeFog(fog, eye) {
    this.deep = 0;
    if (!fog || !eye) return 0;
    const R = this.radius, H = bankHeight(R);
    let deep = 0, within = false;
    for (const [x, y, z, k] of this.banks) {
      const across = Math.hypot(eye.x - x, eye.z - z) / R;
      const up = (eye.y - y) / H;
      // Inside the body's drum (its shader's BANK_PAD, with a margin): only
      // then do its far faces need drawing at all.
      within ||= across < 1.06 && up > -0.05 && up < 1.06;
      deep = Math.max(deep, k * ease01((1 - across) / 0.2) * ease01((0.85 - up) / 0.35));
    }
    if (deep > 0.01) {
      // Closed in by extinction, not by distance: halfway into the bank is
      // halfway to its thickness, not halfway from the sea's 14 km haze.
      const far = Math.max(R * BANK_SIGHT, 45);
      fog.near = 1 / (1 / Math.max(fog.near, 1) + (1 / (far * 0.05) - 1 / Math.max(fog.near, 1)) * deep);
      fog.far = 1 / (1 / fog.far + (1 / far - 1 / fog.far) * deep);
      const A = FX_UNIFORMS.uAmb.value, S = FX_UNIFORMS.uSun.value;
      // The body's own grey deep inside it: the sky's fill, little sun.
      _bankGrey.setRGB(
        SCREEN_GREY[0] * 0.3 * (A.r * 0.55 + S.r * 0.08),
        SCREEN_GREY[1] * 0.3 * (A.g * 0.55 + S.g * 0.08),
        SCREEN_GREY[2] * 0.3 * (A.b * 0.55 + S.b * 0.08),
      );
      fog.color.lerp(_bankGrey, deep);
    }
    this.deep = deep;
    // From outside every bank, the near faces alone: the far ones would only
    // be shaded to be thrown away, and on software GL that doubles the bill.
    this.body.material.side = within ? THREE.DoubleSide : THREE.FrontSide;
    return deep;
  }

  /**
   * How much of a card at (x, y, z) shows through the banks between it and
   * the eye, 0..1: the smoke its sightline crosses, measured through a drum a
   * little smaller than the body's (the body's rim and crown are thin).
   */
  transmit(eye, x, y, z) {
    if (!this.banks.length || !eye) return 1;
    const R = this.radius * VEIL_REACH, H = bankHeight(this.radius);
    const Hc = H * VEIL_TOP;
    const dx = x - eye.x, dy = y - eye.y, dz = z - eye.z;
    const len = Math.hypot(dx, dy, dz);
    let tau = 0;
    for (const [bx, by, bz, k] of this.banks) {
      const ox = (eye.x - bx) / R, oz = (eye.z - bz) / R, oy = (eye.y - by) / Hc;
      const ux = dx / R, uz = dz / R, uy = dy / Hc;
      let s0 = 0, s1 = 1;
      const a = ux * ux + uz * uz, b = ox * ux + oz * uz, c = ox * ox + oz * oz - 1;
      if (a > 1e-12) {
        const disc = b * b - a * c;
        if (disc <= 0) continue;
        const q = Math.sqrt(disc);
        s0 = Math.max(s0, (-b - q) / a);
        s1 = Math.min(s1, (-b + q) / a);
      } else if (c > 0) continue;
      if (Math.abs(uy) > 1e-12) {
        const ta = -oy / uy, tb = (1 - oy) / uy;
        s0 = Math.max(s0, Math.min(ta, tb));
        s1 = Math.min(s1, Math.max(ta, tb));
      } else if (oy < 0 || oy > 1) continue;
      if (s1 > s0) tau += (s1 - s0) * len * k;
    }
    return tau > 0 ? Math.exp(-tau * (BANK_TAU / H) * VEIL_DENSITY) : 1;
  }
}

const ease01 = (t) => {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
};
const _bankGrey = new THREE.Color();
// How far an eye deep in a bank sees, as a share of the bank's reach: about
// two destroyer lengths in a naval screen, a tank's own length or so in a
// land one. The server's concealment is the rule (inSmoke); this is only
// what it looks like.
const BANK_SIGHT = 0.5;
// The veil's drum against the body's: its reach and height, and how thick
// its smoke runs on average (the body's top rolls and its rim frays).
const VEIL_REACH = 0.85;
const VEIL_TOP = 0.75;
const VEIL_DENSITY = 0.8;

// How many screens draw their body at once: more than any round lays (a
// screen lasts under half a minute and every hull's generator has a long
// cooldown).
const BANK_MAX = 24;
// How tall the bank's drum stands: a seventh of its reach plus a floor. A
// 520 m naval screen's drum is 77 m, its rolling top averaging about 60 m,
// over a battleship's fighting top; a tank's 55 m cloud stands about 12 m.
const BANK_RISE = 0.14;
const BANK_FLOOR_M = 4;
export const bankHeight = (R) => R * BANK_RISE + BANK_FLOOR_M;
// The bank's thickness, as the optical depth of a full drum's height of
// smoke. Its rolling top leaves a sightline straight down the middle about
// nine tenths covered; any slanting one crosses more, and one skimming the
// water through the bank sees nothing.
const BANK_TAU = 2.6;

const BANK_VERT = /* glsl */ `
attribute vec4 iBank;   // xyz: the bank's foot, on the water (or the ground); w: its reach (m)
attribute float iLife;  // 0..1: how thick it stands (laid, full, burning out)
uniform float uHeight;
varying vec3 vWorld;
varying vec4 vBank;
varying float vLife;
#include <fog_pars_vertex>

void main() {
  vLife = iLife;
  if (iLife < 0.003) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  // A drum a shade wider and taller than the smoke it holds (its flat facets
  // sit inside the true circle), its floor a hand's breadth over the water
  // so the sea never hides it from a sightline inside the bank.
  vec3 wp = iBank.xyz + position * vec3(iBank.w * BANK_PAD, uHeight * BANK_PAD, iBank.w * BANK_PAD);
  wp.y = max(wp.y, iBank.y + BANK_FLOOR);
  vWorld = wp;
  vBank = iBank;
  vec4 mvPosition = viewMatrix * vec4(wp, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const BANK_FRAG = /* glsl */ `
uniform sampler2D uNoise;
uniform vec3 uAmb;
uniform vec3 uSun;
uniform vec3 uSunDir;
uniform vec3 uTint;
uniform float uHeight;
uniform float uTau;
uniform float uTime;
varying vec3 vWorld;
varying vec4 vBank;
varying float vLife;
#include <fog_pars_fragment>
${FX_OUT_GLSL}

void main() {
  // The sightline in the bank's own frame, where the bank is the unit drum:
  // the unit circle across, 0..1 from the water up.
  vec3 axes = vec3(vBank.w, uHeight, vBank.w);
  vec3 ro = cameraPosition;
  vec3 rd = normalize(vWorld - ro);
  vec3 o = (ro - vBank.xyz) / axes;
  vec3 d = rd / axes;
  // Every sightline is shaded once: on the drum's near face from outside,
  // its far face from within.
  bool within = dot(o.xz, o.xz) < BANK_PAD * BANK_PAD && o.y > 0.0 && o.y < BANK_PAD;
  if (within == gl_FrontFacing) discard;
  // Where it enters and leaves the drum's wall...
  float t0 = 0.0, t1 = 1e9;
  float a = dot(d.xz, d.xz), b = dot(o.xz, d.xz), c = dot(o.xz, o.xz) - 1.0;
  if (a > 1e-12) {
    float disc = b * b - a * c;
    if (disc <= 0.0) discard;
    float s = sqrt(disc);
    t0 = (-b - s) / a;
    t1 = (-b + s) / a;
  } else if (c > 0.0) discard;
  // ... and its floor (the water) and roof.
  if (abs(d.y) > 1e-9) {
    float ta = -o.y / d.y, tb = (1.0 - o.y) / d.y;
    t0 = max(t0, min(ta, tb));
    t1 = min(t1, max(ta, tb));
  } else if (o.y < 0.0 || o.y > 1.0) discard;
  t0 = max(t0, 0.0);
  if (t1 <= t0) discard;
  // Walk it, near to far. The bank's top is flat across its middle and rolls
  // where the billow noise lifts or sinks it; its wall falls steeply near the
  // rim and frays outward; the fine grain roughs every edge. Each step is lit
  // for where it sits: the faces of the rolling top that lean toward the sun
  // catch it, the smoke deep under the top sits in its own shade, and what
  // lies nearer hides what lies behind. The walk starts at a random offset
  // each pixel, so its steps smear into a fine grain rather than stacking
  // into contour bands.
  #ifdef LITE
    float dt = (t1 - t0) / float(STEPS);
  #else
    // A step every few percent of the bank's reach: a sightline grazing
    // along the whole wall takes the most, one dropping through the top the
    // fewest.
    float steps = clamp(ceil((t1 - t0) / (vBank.w * 0.035)), 6.0, float(STEPS));
    float dt = (t1 - t0) / steps;
  #endif
  float jit = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
  float scale = 1.0 / (vBank.w * 1.2);
  vec2 drift = vec2(0.011, 0.007) * uTime + vBank.xz * 0.00173;
  vec2 sunward = normalize(uSunDir.xz + vec2(1e-4, 0.0)) * 0.03;
  float sig = uTau / uHeight * vLife;
  #ifdef LITE
    // Phones and software GL: one fetch a step, and the light laid on once
    // by how high the smoke the sightline crossed hangs.
    float tau = 0.0, hi = 0.0;
    for (int i = 0; i < STEPS; i++) {
      vec3 p = ro + rd * (t0 + (float(i) + jit) * dt);
      vec3 q = (p - vBank.xyz) / axes;
      float billow = texture2D(uNoise, p.xz * scale + drift).r;
      float r2 = dot(q.xz, q.xz);
      float top = (0.4 + 0.66 * smoothstep(0.2, 0.8, billow)) * sqrt(max(0.0, 1.0 - r2 * r2 * r2));
      float rho = smoothstep(0.0, 0.26, top - q.y) * smoothstep(1.0, 0.72, sqrt(r2));
      tau += rho;
      hi += rho * clamp(q.y, 0.0, 1.0);
    }
    float h = hi / max(tau, 1e-4);
    float T = exp(-tau * dt * sig);
    vec3 col = (1.0 - T) * (uAmb * 0.62 * (0.7 + 0.4 * h) + uSun * 0.3 * (0.2 + 0.9 * h));
  #else
    float T = 1.0;
    vec3 col = vec3(0.0);
    for (int i = 0; i < STEPS; i++) {
      // Walked its share, or seen nothing past here: an eye inside the bank
      // goes blind in a few steps.
      if (float(i) >= steps || T < 0.01) break;
      vec3 p = ro + rd * (t0 + (float(i) + jit) * dt);
      vec3 q = (p - vBank.xyz) / axes;
      vec2 uv = p.xz * scale + drift;
      float billow = texture2D(uNoise, uv).r;
      float toSun = texture2D(uNoise, uv + sunward).r;
      float grain = texture2D(uNoise, uv * 2.3 + q.y * 0.37).g;
      float r2 = dot(q.xz, q.xz);
      float top = (0.4 + 0.66 * smoothstep(0.2, 0.8, billow)) * sqrt(max(0.0, 1.0 - r2 * r2 * r2));
      float rho = smoothstep(0.0, 0.26, top - q.y + (grain - 0.5) * 0.14)
                * smoothstep(1.0, 0.72, sqrt(r2) + (grain - 0.5) * 0.18);
      float a = 1.0 - exp(-rho * dt * sig);
      float under = max(top - q.y, 0.0);
      float sunlit = clamp(0.55 + (billow - toSun) * 9.0, 0.1, 1.0) * exp(-under * 5.0);
      float fill = 0.7 + 0.4 * clamp(q.y / max(top, 0.05), 0.0, 1.0);
      col += T * a * (uAmb * 0.62 * fill + uSun * 0.3 * (0.2 + 1.3 * sunlit));
      T *= 1.0 - a;
    }
  #endif
  float cover = 1.0 - T;
  if (cover < 0.002) discard;
  // Lit as the other gas is (flipbooks.js): the smoke's grey under the sky's
  // fill and its key light.
  vec3 gas = uTint * 0.3 * col / cover;
  #ifdef USE_FOG
    #ifdef FOG_EXP2
      float fogF = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
    #else
      float fogF = smoothstep(fogNear, fogFar, vFogDepth);
    #endif
    gas = mix(gas, fogColor, fogF);
  #endif
  gl_FragColor = vec4(fxOut(gas) * cover, cover);
}
`;

// Every screen's body in one instanced draw: a drum, stretched per bank by
// the vertex shader and shaded by the fragment's walk through it.
function bankBody(lite) {
  const ball = new THREE.CylinderGeometry(1, 1, 1, 32, 1).translate(0, 0.5, 0);
  const g = new THREE.InstancedBufferGeometry();
  g.setIndex(ball.getIndex());
  g.setAttribute("position", ball.getAttribute("position"));
  const bank = new THREE.InstancedBufferAttribute(new Float32Array(BANK_MAX * 4), 4);
  const life = new THREE.InstancedBufferAttribute(new Float32Array(BANK_MAX), 1);
  bank.setUsage(THREE.DynamicDrawUsage);
  life.setUsage(THREE.DynamicDrawUsage);
  g.setAttribute("iBank", bank);
  g.setAttribute("iLife", life);
  g.instanceCount = 0;
  ball.dispose();
  const m = new THREE.ShaderMaterial({
    uniforms: {
      ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog),
      uNoise: { value: noiseTexture() },
      uAmb: FX_UNIFORMS.uAmb,
      uSun: FX_UNIFORMS.uSun,
      uSunDir: FX_UNIFORMS.uSunDir,
      uTint: { value: new THREE.Vector3(...SCREEN_GREY) },
      uHeight: { value: 1 },
      uTau: { value: BANK_TAU },
      uTime: { value: 0 },
    },
    // Phones and software GL take fewer, cheaper steps: the bank can fill
    // the screen.
    defines: { STEPS: lite ? 3 : 24, BANK_PAD: "1.03", BANK_FLOOR: "0.4", ...(lite ? { LITE: "" } : {}) },
    vertexShader: BANK_VERT,
    fragmentShader: BANK_FRAG,
    transparent: true,
    depthWrite: false,
    side: THREE.FrontSide, // and both, the far faces for an eye inside a bank (squeezeFog)
    fog: true,
    // Premultiplied, as the cards are.
    blending: THREE.CustomBlending,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
    blendSrcAlpha: THREE.OneFactor,
    blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
  });
  const mesh = new THREE.Mesh(g, m);
  mesh.frustumCulled = false; // the banks span the map; the drum sits at the origin
  mesh.visible = false;
  // Over the sea (-8), under every card: the cards draw after the smoke,
  // each sunk into it by as much smoke as lies between it and the eye
  // (Smoke.transmit).
  mesh.renderOrder = -1;
  return mesh;
}

// The band floor's ramp: a contrail comes in over this much climb above
// contrailAltM, so a plane skimming the floor doesn't flicker one on and off.
const CONTRAIL_RAMP_M = 200;
// Below this airspeed a remote plane's derived G is noise (roadmap #45).
const STREAMER_MIN_MS = 40;
const _trailOff = new THREE.Vector3();

// A setting the config left out (or zeroed) takes its default.
const setting = (v, dflt) => (v > 0 ? v : dflt);

// The outboard engines' contrail ids: the inboard pair's (id*2, id*2+1)
// mirrored below zero, so no plane's outer engine ever takes up another's
// inner one, and every two-engine id stays where it always was.
const outboardKey = (k) => -1 - k;

/**
 * What planes leave hanging in the sky: contrails off the engines above the
 * band floor (roadmap #45), streamers snapping off the wingtips in a hard
 * pull, and the comeback ace's gold ribbon (roadmap #14). Each engine, each
 * wingtip and each ace writes its own trail ribbon (streaks.js), so a
 * contrail is one unbroken rope that spreads, drifts downwind and hangs —
 * the high fight written across the blue for the whole map to read — not a
 * ladder of see-through crates.
 *
 * Feed every flying plane through plane() each frame and drop() the ones
 * that aren't (shot down, bailed, gone); the trails do their own cadence.
 * `fx` is the config's effects block (contrailAltM, contrailFadeSec,
 * streamerG, streamerMaxG). `lite` is a CPU rasterizer (gfx.js
 * softwareRenderer): it pays for every vertex, so its trails lay their
 * joints half as often into buffers half the size — the same sky, coarser.
 */
export class PlaneTrails {
  constructor(scene, fx = {}, { lite = false } = {}) {
    this.altM = setting(fx.contrailAltM, 900);
    this.streamerG = setting(fx.streamerG, 3.5);
    this.streamerMaxG = Math.max(this.streamerG + 0.1, setting(fx.streamerMaxG, 6));
    // Three buffers, one per lifetime: a ribbon buffer sheds its dead from
    // the front, oldest first, so a streamer's 0.7 s laid behind a 22 s
    // contrail would sit in the buffer (and the vertex count) for the
    // contrail's whole life. Seven planes at altitude lay ~1500 contrail
    // segments (a four-engine bomber twice a fighter's); a bigger furball or
    // a bomber stream goes sparse past 80% rather than dearer.
    const spread = lite ? 2 : 1;
    this.contrails = new TrailRibbons(scene, lite ? 1024 : 2048, {
      heads: 96,
      sparse: true, // a long high fight lays them sparser, never shorter
      spread,
      tune: { contrail: { life: setting(fx.contrailFadeSec, 22) } },
    });
    this.streamers = new TrailRibbons(scene, lite ? 384 : 1024, { heads: 96, spread });
    // The ace's gold holds a little more than a pixel at any range: a bounty
    // you can't see is no bounty.
    this.gold = new TrailRibbons(scene, 256, { heads: 4, minPx: 1.2 });
    // A hit airframe's smoke and a holed tank's vapour: one unbroken rope
    // each off the wounded bird (a puff dropped every few frames behind a
    // fighter doing 100 m/s hung a dotted line of balls).
    this.smoke = new TrailRibbons(scene, lite ? 512 : 1536, { heads: 96, spread });
  }

  /**
   * A damaged plane's trails this frame: `smoke` 0..1 (a sick engine's thin
   * grey wisp to a dying one's black stream; 0 for none), `vapor` whether
   * fuel is venting. `pos` and `vel` as for plane(). A wreck going down trails
   * them too: the smoke is the damage's, not the engine's.
   */
  damage(id, pos, vel, smoke, vapor) {
    const S = id * 2, V = S + 1;
    if (smoke > 0) {
      this.smoke.feed(S, pos.x, pos.y, pos.z, vel.x, vel.y, vel.z, TRAIL_KIND.damageSmoke, 0.6 + 0.7 * smoke, 0.4 + 0.6 * smoke);
    } else {
      this.smoke.end(S);
    }
    if (vapor) this.smoke.feed(V, pos.x, pos.y, pos.z, vel.x, vel.y, vel.z, TRAIL_KIND.fuelVapor);
    else this.smoke.end(V);
  }

  /** It isn't trailing smoke any more (gone, or down): the ropes end and hang. */
  dropSmoke(id) {
    this.smoke.end(id * 2);
    this.smoke.end(id * 2 + 1);
  }

  /**
   * One flying plane this frame. `id` is its player id; `geom` where its
   * trails leave the airframe (planes.js CLASS_TRAILS: the engines' and the
   * wingtips' offsets across the span, plus the `outboard` pair's on a
   * four-engine bomber); `pos`, `vel` and `quat` where it is, how it's going
   * and how it sits (a THREE.Quaternion); `g` the load it's pulling; `ace`
   * whether it's the comeback ace.
   */
  plane(id, geom, pos, vel, quat, g, ace = false) {
    const L = id * 2, R = L + 1;
    if (pos.y > this.altM) {
      const k = Math.min(1, (pos.y - this.altM) / CONTRAIL_RAMP_M);
      const scale = 0.6 + 0.4 * k;
      this._pair(this.contrails, L, R, geom.nacelles, pos, vel, quat, TRAIL_KIND.contrail, scale, k);
      // A Fortress box at altitude is four white lines a ship, not a
      // fighter's two: the stream that says "bombers" from ten miles off.
      if (geom.outboard) this._pair(this.contrails, outboardKey(L), outboardKey(R), geom.outboard, pos, vel, quat, TRAIL_KIND.contrail, scale, k);
      else this._endOutboard(L, R); // a respawn into a two-engine type
    } else {
      // Dive through the floor and the trail ends there (the classic tell).
      this.contrails.end(L);
      this.contrails.end(R);
      this._endOutboard(L, R);
    }
    const sp = Math.sqrt(vel.x * vel.x + vel.y * vel.y + vel.z * vel.z);
    if (g > this.streamerG && sp > STREAMER_MIN_MS) {
      // Bolder the deeper past the threshold: a 4 g turn draws a faint
      // thread, a 6 g break two hard white lines.
      const w = Math.min(1, (g - this.streamerG) / (this.streamerMaxG - this.streamerG));
      this._pair(this.streamers, L, R, geom.tip, pos, vel, quat, TRAIL_KIND.streamer, 0.6 + 0.6 * w, 0.35 + 0.65 * w);
    } else {
      this.streamers.end(L);
      this.streamers.end(R);
    }
    if (ace) this.gold.feed(id, pos.x, pos.y, pos.z, vel.x, vel.y, vel.z, TRAIL_KIND.aceGold);
    else this.gold.end(id);
  }

  _endOutboard(L, R) {
    this.contrails.end(outboardKey(L));
    this.contrails.end(outboardKey(R));
  }

  // A trail off each side of the airframe, `half` metres out along the span.
  _pair(trails, L, R, half, pos, vel, quat, kind, scale, opacity) {
    const o = _trailOff.set(0, 0, half).applyQuaternion(quat);
    trails.feed(L, pos.x + o.x, pos.y + o.y, pos.z + o.z, vel.x, vel.y, vel.z, kind, scale, opacity);
    trails.feed(R, pos.x - o.x, pos.y - o.y, pos.z - o.z, vel.x, vel.y, vel.z, kind, scale, opacity);
  }

  /** A plane that isn't flying any more: its trails end where it was last drawn and hang. */
  drop(id) {
    const L = id * 2, R = L + 1;
    this.contrails.end(L);
    this.contrails.end(R);
    this._endOutboard(L, R);
    this.streamers.end(L);
    this.streamers.end(R);
    this.gold.end(id);
  }

  /**
   * Age everything by dt (the effects clock: the kill-confirm's slow motion
   * slows it), drift it on the round's wind, and sort it among the other
   * see-through draws from `eye` (the camera's position).
   */
  update(dt, windX = 0, windZ = 0, eye = null) {
    this.contrails.update(dt, windX, windZ, eye);
    this.streamers.update(dt, windX, windZ, eye);
    this.gold.update(dt, windX, windZ, eye);
    this.smoke.update(dt, windX, windZ, eye);
  }
}

/**
 * How hard a plane is smoking, 0..1 (0: clean), from its component damage
 * `d` ({ engine, fuel, … }, a missing field is healthy) and its HP fraction:
 * a sick engine or a battered airframe starts a thin wisp, a dying one pours
 * black. Whether it vents fuel rides along.
 */
export function damageSmoke(d, hpFrac) {
  const engine = d?.engine ?? 1;
  const k = Math.max(engine < 0.7 ? (0.7 - engine) / 0.7 : 0, hpFrac < 0.35 ? (0.35 - hpFrac) / 0.35 : 0);
  return { smoke: k > 0 ? 0.35 + 0.65 * Math.min(1, k) : 0, vapor: (d?.fuel ?? 1) < 0.85 };
}

// Sparks off struck steel: each a white-hot fleck smeared along its flight,
// the way the eye (and every gun camera) sees it, cooling to amber as it
// arcs down. Light, not blocks — the tracers' streaks (streaks.js).
const SPARK_HOT = new THREE.Color(0xfff1d8);
const SPARK_COOL = new THREE.Color(0xff7a2e);
// Seconds of flight a spark is smeared over.
const SPARK_SMEAR = 0.022;

export class Sparks {
  constructor(scene, capacity = 320) {
    this.batch = new StreakBatch(scene, capacity, { mode: "glow", minPx: 1.1, fadePow: 0.5 });
    this.pool = new SoAPool(capacity, { f32: ["px", "py", "pz", "vx", "vy", "vz", "life", "maxLife", "w", "g"] });
    this._c = new THREE.Color();
  }

  /**
   * `count` sparks off `pos`, flung up to `speed` m/s (optionally biased
   * toward `dir`; spread 1 = any way, 0 = straight along it), living about
   * `life` s, `width` m thick, falling at `gravity`.
   */
  burst(pos, { count = 10, speed = 50, life = 0.35, width = 0.08, gravity = 9.81, dir = null, spread = 1 } = {}) {
    const p = this.pool;
    const { at, skip } = p.makeRoom(count);
    const { px, py, pz, vx, vy, vz, life: lifeA, maxLife, w, g } = p.f;
    for (let i = skip, k = at; i < count; i++, k++) {
      let x = Math.random() * 2 - 1, y = Math.random() * 2 - 1, z = Math.random() * 2 - 1;
      let l = Math.sqrt(x * x + y * y + z * z) || 1;
      x /= l; y /= l; z /= l;
      if (dir) {
        x = x * spread + dir.x * (1 - spread);
        y = y * spread + dir.y * (1 - spread);
        z = z * spread + dir.z * (1 - spread);
        l = Math.sqrt(x * x + y * y + z * z) || 1;
        x /= l; y /= l; z /= l;
      }
      const sp = speed * (0.35 + Math.random() * 0.65);
      px[k] = pos.x; py[k] = pos.y; pz[k] = pos.z;
      vx[k] = x * sp; vy[k] = y * sp; vz[k] = z * sp;
      lifeA[k] = maxLife[k] = life * (0.5 + Math.random() * 0.5);
      w[k] = width * (0.6 + Math.random() * 0.8);
      g[k] = gravity;
    }
  }

  update(dt, eye = null) {
    const p = this.pool;
    const { px, py, pz, vx, vy, vz, life, maxLife, w, g } = p.f;
    const b = this.batch;
    const c = this._c;
    const damp = Math.max(0, 1 - 1.2 * dt);
    b.begin();
    let live = 0;
    for (let k = 0; k < p.count; k++) {
      const l = life[k] - dt;
      if (l <= 0) continue;
      if (live !== k) p.copySlot(k, live);
      life[live] = l;
      const nvx = vx[live] * damp, nvy = (vy[live] - g[live] * dt) * damp, nvz = vz[live] * damp;
      vx[live] = nvx; vy[live] = nvy; vz[live] = nvz;
      const x = (px[live] += nvx * dt), y = (py[live] += nvy * dt), z = (pz[live] += nvz * dt);
      const t = l / maxLife[live]; // 1 fresh .. 0 spent
      c.copy(SPARK_COOL).lerp(SPARK_HOT, t);
      const r = w[live] * (0.5 + 0.5 * t);
      b.push(x, y, z, r, x - nvx * SPARK_SMEAR, y - nvy * SPARK_SMEAR, z - nvz * SPARK_SMEAR, r * 0.6, c.r, c.g, c.b, 1.4 * t);
      live++;
    }
    p.count = live;
    b.end(eye);
  }
}

// The gas's colours, as multipliers of the flipbooks' neutral grey (the sky
// lights them on top; the baked grey is a mid grey, so white wants about
// twice it): oily black off burning fuel, brown-black off torn-up earth, the
// brown of the earth a burst throws up, the steppe's dust, cordite's
// brown-grey, and white for spray, steam and vapour.
const SMOKE_BLACK = [0.26, 0.25, 0.24];
const SMOKE_EARTH = [0.5, 0.44, 0.37];
const DUST_EARTH = [0.85, 0.7, 0.5];
const DUST_TAN = [1.2, 1.05, 0.82];
const CORDITE = [1.3, 1.22, 1.08];
const SPRAY = [1.7, 1.72, 1.78];
const WHITE = [1.9, 1.9, 1.95];
// A smoke screen's bank: a light grey haze on the water.
export const SCREEN_GREY = [1.55, 1.6, 1.68];
// The fireballs burn a shade past the baked sheet, so they glow against a
// noon sky as well as a dusk one.
const FIREBALL_EMIT = 1.35;
// How hard a fire's heat lifts its flame (m/s). Against a fighter's 100 m/s
// it barely tips a streaming torch up; a burner creeping along at a few
// metres a second leans back, and a standing one burns straight up.
const FLAME_LIFT = 5;
// What's still thrown as blocks: scraps of a dead airframe or hull, and
// clods of earth.
// A repair party's glint: the muzzle star's white-hot heart, pulled green.
const REPAIR_GREEN = [0.45, 1.5, 0.55];
const REPAIR_BLOCKS = [0x7cfc7c, 0xbfffbf, 0x4fd14f];
const SCRAP = [0x2e2e2e, 0x3a3a3a, 0x222222, 0x4a4036];
const CLODS = [0x5a4a32, 0x6b5b40, 0x44382a];

const rnd = (a) => (Math.random() * 2 - 1) * a;
const coin = () => Math.random() < 0.5;
// Gun splashes ride a budget: a fighter's battery strafing the sea puts sixty
// rounds a second into it, and every spout is a card the fireballs and
// the shell columns share a pool with. The bucket holds a burst's worth and
// refills at a rate that still draws a stream walking across the water as an
// unbroken line of spouts; past it, rounds go in without one.
const WET_BURST = 24;
const WET_PER_SEC = 90;
const UP = new THREE.Vector3(0, 1, 0); // read-only: a jet's axis
const STAND_UP = [0, 1, 0];
const GUN_JET_K = 0.55; // a gun spout's width against the shell column's

/**
 * Every burst, fire and puff of smoke in the world. The recipes below say
 * what each event throws; the soft particles (flipbooks.js) draw the gas and
 * the fire, the Sparks their light, and the block pool — the world's
 * original particles — whatever is solid. Until the flipbooks land, under
 * ?boxes, or with `useSprites` off (the probes' A/B switch), every recipe
 * throws its old blocks instead.
 */
export class Particles {
  constructor(scene, capacity = 700, { sprites = null } = {}) {
    this.capacity = capacity;
    this.sprites = sprites;
    this.useSprites = true;
    this.sparks = new Sparks(scene);
    this.mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshLambertMaterial({ color: 0xffffff }),
      capacity,
    );
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(capacity * 3),
      3,
    );
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);

    // Self-lit glow layer for fire: unlit + additive so muzzle flash blooms
    // bright at night (overlapping particles sum to a white-hot core), unlike
    // the scene-lit cubes above which go dark without daylight.
    this.glowCap = 256;
    this.glowMesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        fog: false,
      }),
      this.glowCap,
    );
    this.glowMesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(this.glowCap * 3),
      3,
    );
    this.glowMesh.frustumCulled = false;
    scene.add(this.glowMesh);

    this.dummy = new THREE.Object3D();
    this.color = new THREE.Color();
    // One live particle is a slot across these arrays — no object per spark.
    // A single broadside pushes ~170 through here and a busy fight keeps the
    // pool pinned at capacity, so this is the difference between garbage that
    // scales with how loud the battle is and garbage that does not exist.
    this.parts = new SoAPool(capacity, {
      f32: ["px", "py", "pz", "vx", "vy", "vz", "life", "maxLife", "size", "gravity", "drag", "spin"],
      u32: ["color"],
      u8: ["flags"], // bit0 grow, bit1 glow
    });
    this._dir = new THREE.Vector3(); // scratch for the spawn direction
    // The share of each burst's blocks actually thrown: 1, or less while the
    // frame governor (governor.js, rung 1) is thinning the effects. Every
    // burst still throws at least one, so nothing goes off without a trace.
    this.density = 1;
    this._wet = WET_BURST; // gun splashes left in the budget (see WET_BURST)
  }

  /** Drawing soft: the flipbooks have landed, and nobody asked for blocks. */
  get soft() {
    return this.useSprites && !!this.sprites?.ready;
  }

  spawn(pos, { count, speed, life, size, colors, gravity = 9.81, drag = 0.6, dir = null, spread = 1, spin = null, grow = false, glow = false }) {
    const p = this.parts;
    if (this.density < 1) count = Math.max(1, Math.round(count * this.density));
    // Evict for the whole batch in one move rather than once per particle.
    const { at, skip } = p.makeRoom(count);
    const flagBits = (grow ? 1 : 0) | (glow ? 2 : 0);
    const fp = p.f;
    const px = fp.px, py = fp.py, pz = fp.pz;
    const vx = fp.vx, vy = fp.vy, vz = fp.vz;
    const lifeA = fp.life, maxLifeA = fp.maxLife, sizeA = fp.size;
    const gravA = fp.gravity, dragA = fp.drag, spinA = fp.spin;
    const colorA = p.u.color, flagsA = p.b.flags;
    const d = this._dir;
    for (let i = skip, w = at; i < count; i++, w++) {
      // Random sphere, optionally biased toward `dir` (spread 1 = fully
      // random, 0 = a tight jet along dir) for directional blasts.
      d.set(Math.random() * 2 - 1, Math.random() * 2 - 1, Math.random() * 2 - 1).normalize();
      if (dir) d.lerp(dir, 1 - spread).normalize();
      const sp = speed * (0.3 + Math.random() * 0.7);
      px[w] = pos.x; py[w] = pos.y; pz[w] = pos.z;
      vx[w] = d.x * sp; vy[w] = d.y * sp; vz[w] = d.z * sp;
      lifeA[w] = life * (0.6 + Math.random() * 0.4);
      maxLifeA[w] = life;
      sizeA[w] = size * (0.6 + Math.random() * 0.8);
      gravA[w] = gravity;
      dragA[w] = drag;
      // Low, steady tumble for smoke; chaotic for debris (default).
      spinA[w] = spin != null ? spin * (0.5 + Math.random()) : Math.random() * 4;
      colorA[w] = colors[(Math.random() * colors.length) | 0];
      flagsA[w] = flagBits;
    }
  }

  // Solid scraps flung out of a burst: small, tumbling, falling.
  _scrap(pos, count, speed, colors, size, gravity = 9.81) {
    this.spawn(pos, { count, speed, life: 1.7, size, colors, gravity, drag: 0.3 });
  }

  // The lingering smoke a burst leaves: it thickens as the fireball dies,
  // billows up and out, and drifts off downwind. Sorted behind the fire.
  _pall(x, y, z, size, tint, { alpha = 0.8, speed = 0.45 } = {}) {
    // It comes up as the fireball goes to smoke, already a billow (the
    // flipbook's dense early puff would show as a ball behind the fire).
    this.sprites.spawn("smoke", x, y, z, {
      size, grow: 1.2, speed, frame: 10, delay: 0.45, fadeIn: 0.5, alpha, rise: 2.6, wind: 1,
      tint, mirror: coin(), roll: rnd(0.35), spin: rnd(0.06), bias: size * 0.25, push: 0.45,
    });
  }

  /**
   * An airframe, a lorry or a gun going up: a white flash and a fireball
   * cooling through orange to black, a second lobe a beat behind it, the
   * smoke that climbs off afterwards, sparks, and scraps flung out.
   */
  explosion(pos, { scale = 1 } = {}) {
    if (!this.soft) return this._blockExplosion(pos);
    const S = this.sprites, k = scale;
    S.spawn("explosion", pos.x, pos.y, pos.z, {
      size: 15 * k, grow: 0.3, speed: 1.15, rise: 1.2, wind: 0.5,
      mirror: coin(), roll: rnd(0.3), fadeOut: 0.5, emit: FIREBALL_EMIT, push: 0.45, bias: -1,
    });
    // A beat behind and off to one side, so no two bursts are the same ball
    // (and a second lobe still burning as the first goes to smoke).
    S.spawn("explosion", pos.x + rnd(3 * k), pos.y + rnd(2 * k), pos.z + rnd(3 * k), {
      size: 10 * k, grow: 0.4, speed: 1.2, delay: 0.12, rise: 1.6, wind: 0.6,
      mirror: coin(), roll: rnd(0.5), fadeOut: 0.5, emit: FIREBALL_EMIT, push: 0.45, bias: -1.5,
    });
    this._pall(pos.x, pos.y + 1.5 * k, pos.z, 11 * k, SMOKE_BLACK, { alpha: 0.75 });
    this._scrap(pos, 8, 26 * k, SCRAP, 0.55 * k);
    this.sparks.burst(pos, { count: 12, speed: 42 * k, life: 0.5, width: 0.14 * k });
  }

  /**
   * A bomb or a rocket going off — or a missile's warhead. `site` says where:
   * "ground" throws dust and clods round a crater, "water" a column of spray,
   * "air" just the fireball and its smoke.
   */
  bombBlast(pos, { site = "ground" } = {}) {
    if (!this.soft) return this._blockBombBlast(pos);
    if (site === "water") return this._waterBurst(pos, 1.1);
    this._groundBurst(pos, 1, site === "air");
  }

  /**
   * An 88 bursting in open sky: a spark, a short orange flash, and the black
   * puff that hangs where it burst, drifting downwind and thinning — the
   * flak field's smudges round a bomber stream. Soft only: the block
   * particles never had one, and a struggling device under ?boxes gets no
   * new load.
   */
  flakBurst(pos) {
    if (!this.soft) return;
    this.sprites.spawn("flak", pos.x, pos.y, pos.z, {
      size: 11, grow: 0.5, life: 6.5, fadeOut: 3, wind: 1, rise: 0.15,
      mirror: coin(), roll: rnd(0.6), emit: FIREBALL_EMIT, push: 0.3,
    });
  }

  /** A main-battery or tank round bursting on the ground (or a hull going up). */
  shellBlast(pos, { site = "ground" } = {}) {
    if (!this.soft) return this._blockShellBlast(pos);
    if (site === "water") return this._waterBurst(pos, 1.6);
    this._groundBurst(pos, 1.35, site === "air");
  }

  _groundBurst(pos, k, air) {
    const S = this.sprites;
    const lift = air ? 0 : 2 * k;
    S.spawn("explosion", pos.x, pos.y + lift, pos.z, {
      size: 22 * k, grow: 0.35, speed: 1.05, rise: 1.4, wind: 0.5,
      mirror: coin(), roll: rnd(0.25), fadeOut: 0.6, emit: FIREBALL_EMIT, push: 0.5, bias: -1,
    });
    S.spawn("explosion", pos.x + rnd(5 * k), pos.y + lift + (air ? rnd(3) : 1.5 * k + rnd(2)), pos.z + rnd(5 * k), {
      size: 15 * k, grow: 0.45, speed: 1.15, delay: 0.1, rise: 2, wind: 0.6,
      mirror: coin(), roll: rnd(0.4), fadeOut: 0.6, emit: FIREBALL_EMIT, push: 0.5, bias: -1.5,
    });
    this._pall(pos.x, pos.y + lift + 1.5 * k, pos.z, 16 * k, air ? SMOKE_BLACK : SMOKE_EARTH);
    if (!air) {
      // Earth thrown out round the crater in a low brown wall, rolling both
      // ways and settling as the smoke climbs off it.
      const n = k > 1.2 ? 3 : 2;
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        S.spawn("dust", pos.x + Math.cos(a) * 4 * k, pos.y, pos.z + Math.sin(a) * 4 * k, {
          size: 30 * k, grow: 0.35, speed: 1.1, delay: i * 0.05, alpha: 0.95, tint: DUST_EARTH,
          mirror: i & 1, wind: 0.8, rise: 0.3, fadeOut: 0.8, push: 0.5, bias: k,
        });
      }
      // Clods: up fast and raining back down.
      this._scrap(pos, Math.round(14 * k), 38 * k, CLODS, 0.9 * k, 22);
    }
    this.sparks.burst(pos, { count: Math.round(10 * k), speed: 50 * k, life: 0.5, width: 0.16 * k });
  }

  // A column of water: climbs off the surface, tops out, falls back as spray,
  // with a lower column beside it (a burst in the sea is a cluster, not a
  // post) and mist rolling off its foot. A heavier round's stands taller and
  // hangs longer.
  _waterBurst(pos, k) {
    const S = this.sprites;
    const speed = k > 2 ? 0.55 : k > 1.2 ? 0.75 : 0.9;
    S.spawn("splash", pos.x, pos.y, pos.z, {
      size: 24 * k, speed, mirror: coin(), roll: rnd(0.05), alpha: 0.95, fadeOut: 0.6, tint: SPRAY, push: 0.12,
    });
    S.spawn("splash", pos.x + rnd(4 * k), pos.y, pos.z + rnd(4 * k), {
      size: 16 * k, speed: speed * 1.1, frame: 3, mirror: coin(), roll: rnd(0.08), alpha: 0.9, fadeOut: 0.6, tint: SPRAY, push: 0.12,
    });
    S.spawn("smoke", pos.x, pos.y + 0.5, pos.z, {
      size: 14 * k, grow: 0.8, speed: 0.5, frame: 18, delay: 0.3, alpha: 0.4, fadeIn: 0.5, tint: SPRAY,
      wind: 1, rise: 0.5, mirror: coin(), push: 0.2, bias: 2 * k,
    });
  }

  /**
   * A shell into the sea. `big`: naval fall-of-shot, the tall column legible
   * at the 3-8 km where a captain walks main-battery fire onto a target (G3)
   * — reading the short and the over is the aim-correction loop. `heavy`: a
   * super-heavy's fall of shot (the Yamato's 18.1-inch), a column a third
   * taller again, so her shorts and overs read from twice as far.
   */
  shellSplash(pos, { big = false, heavy = false } = {}) {
    if (!this.soft) return this._blockShellSplash(pos, { big, heavy });
    this._waterBurst(pos, heavy ? 4.3 : big ? 3.2 : 1);
  }

  /** Something small into the water: a downed plane, a fish breaking up. */
  splash(pos) {
    if (!this.soft) return this._blockSplash(pos);
    this._waterBurst(pos, 0.7);
  }

  /**
   * A gun round into the water: a quick white spout `size` metres across its
   * frame (gunSplashSize: the caliber's), up and collapsed in about a second
   * — a stream walking across the sea draws itself as a line of them. A
   * cannon shell's throws a second, lower spout beside the first, a beat
   * behind. Budgeted (WET_BURST); returns whether it was drawn.
   */
  gunSplash(pos, size = 4.4) {
    if (this._wet < 1) return false;
    this._wet -= 1;
    if (!this.soft) {
      this._blockGunSplash(pos, size);
      return true;
    }
    // The shell column's flipbook, played fast and drawn half as wide: a jet,
    // not a pillar. No two the same height, so a burst's spouts read as a
    // ragged line thrown up by rounds, not a picket fence.
    const S = this.sprites;
    const h = size * (0.8 + Math.random() * 0.4);
    S.spawn("splash", pos.x, pos.y, pos.z, {
      size: h, speed: 2.4, frame: 2, stand: STAND_UP, stretch: GUN_JET_K, mirror: coin(), roll: rnd(0.08),
      alpha: 0.85, fadeOut: 0.25, tint: SPRAY, push: 0.1,
    });
    if (size > 5.5) {
      S.spawn("splash", pos.x + rnd(0.15 * size), pos.y, pos.z + rnd(0.15 * size), {
        size: h * 0.65, speed: 2.7, frame: 2, delay: 0.06, stand: STAND_UP, stretch: GUN_JET_K, mirror: coin(), roll: rnd(0.1),
        alpha: 0.8, fadeOut: 0.25, tint: SPRAY, push: 0.1,
      });
    }
    return true;
  }

  /**
   * A round cracking off steel: a white-hot star where it struck and a spray
   * of sparks streaking off, cooling to amber as they fall.
   */
  hitSpark(pos) {
    if (!this.soft) return this._blockHitSpark(pos);
    const e = FX_SHEETS.muzzle.endOn;
    this.sprites.spawn("muzzle", pos.x, pos.y, pos.z, {
      frame: e[(Math.random() * e.length) | 0], size: 1.5 + Math.random() * 0.8, life: 0.06,
      emit: 2.4, roll: Math.random() * Math.PI * 2, push: 0.2, bias: -2,
    });
    this.sparks.burst(pos, { count: 9, speed: 55, life: 0.32, width: 0.07 });
  }

  /**
   * A repair party at work: a green glint winking up off the deck or the
   * engine deck and drifting away — light, so a heal reads on any hull
   * across the steppe or the sea, yours or a spotted enemy's. (It used to
   * be a fountain of green cubes, which read as a hull on fire with
   * something nobody could name.)
   */
  repairGlint(pos) {
    if (!this.soft) {
      this.spawn(pos, { count: 1, speed: 4, life: 0.9, size: 1.3, colors: REPAIR_BLOCKS, gravity: -7, drag: 1.1 });
      return;
    }
    const e = FX_SHEETS.muzzle.endOn;
    this.sprites.spawn("muzzle", pos.x, pos.y, pos.z, {
      frame: e[(Math.random() * e.length) | 0], speed: 0, size: 0.9 + Math.random() * 0.7, life: 0.7,
      fadeIn: 0.12, fadeOut: 0.4, emit: 1.6, tint: REPAIR_GREEN, roll: Math.random() * Math.PI * 2,
      vx: rnd(0.6), vy: 2.2 + Math.random() * 1.6, vz: rnd(0.6), drag: 0.8, push: 0.2, bias: -1,
    });
  }

  /**
   * A dart glancing off the ground: a spurt of earth kicked up where it
   * struck, a few clods, and the spark of steel on stone. No fireball — a
   * solid round doesn't burst, it skips, and its tracer flies on.
   */
  groundSkip(pos, k = 1) {
    if (!this.soft) return this._blockHitSpark(pos);
    // k scales the kick by round weight: 1 for a tank dart, ~0.4 for an
    // autocannon round — a puff and a couple of clods, not a furrow.
    this.sprites.spawn("dust", pos.x, pos.y, pos.z, {
      size: 7 * k, grow: 0.5, speed: 1.2, alpha: 0.85, tint: DUST_EARTH, mirror: coin(),
      wind: 1, rise: 0.6, fadeOut: 0.6, push: 0.4, bias: 1,
    });
    this._scrap(pos, Math.max(2, Math.round(6 * k)), 16, CLODS, 0.45, 18);
    this.sparks.burst(pos, { count: Math.max(2, Math.round(6 * k)), speed: 40, life: 0.25, width: 0.06 });
  }

  /**
   * Naval rifle firing: a lance of flame down the bore, the fireball it
   * throws — white-hot at the muzzle, rolling orange downrange, far bigger
   * than the barrels, after an Iowa-class broadside — and the brown-grey
   * cordite cloud that billows off it, climbs and hangs downwind. `k` scales
   * the whole blast by gun weight (1 = the full battleship broadside; a
   * destroyer's 5in passes ~0.45).
   */
  muzzleBlast(pos, dir, k = 1) {
    if (!this.soft) return this._blockMuzzleBlast(pos, dir, k);
    const S = this.sprites;
    const s = 0.55 + 0.45 * k; // even a small gun keeps some presence
    const side = FX_SHEETS.muzzle.sideOn;
    S.spawn("muzzle", pos.x, pos.y, pos.z, {
      frame: side[(Math.random() * side.length) | 0], size: 30 * s, stretch: 1.4, dir: [dir.x, dir.y, dir.z],
      life: 0.16, fadeOut: 0.08, emit: 2.2, push: 0.1, bias: -3,
    });
    // The fireball: the flipbook's burning middle (past its white flash —
    // the lance is the flash), rolling out along the bore and cooling as it
    // goes, a second bloom a beat behind the first and further out.
    for (const [along, size, v, delay] of [[0.3, 34, 22, 0], [0.62, 26, 34, 0.05]]) {
      const a = along * size * s;
      S.spawn("explosion", pos.x + dir.x * a, pos.y + dir.y * a, pos.z + dir.z * a, {
        size: size * s, grow: 0.55, frame: 11, end: 26, speed: 1.6, delay,
        dir: [dir.x, dir.y, dir.z], stretch: 1.35, // thrown down the bore, not a ball
        vx: dir.x * v * s, vy: dir.y * v * s, vz: dir.z * v * s, drag: 2.5,
        mirror: coin(), roll: rnd(0.15), fadeOut: 0.14, emit: 1.3, push: 0.3, bias: -2,
      });
    }
    for (let i = 0; i < 2; i++) {
      const a = (6 + i * 9) * s, v = (10 + i * 8) * s;
      S.spawn("smoke", pos.x + dir.x * a, pos.y + dir.y * a, pos.z + dir.z * a, {
        size: (22 + i * 6) * s, grow: 1.4, speed: 0.4, frame: 4, fadeIn: 0.12, alpha: 0.9, tint: CORDITE,
        vx: dir.x * v, vy: dir.y * v + 2, vz: dir.z * v, drag: 0.5, rise: 1.2, wind: 1,
        mirror: coin(), roll: rnd(0.4), spin: rnd(0.05), push: 0.3, bias: 2,
      });
    }
  }

  /**
   * A tank's main gun: a short lance of flame, a fireball the size of the
   * turret, a puff of cordite, and — `groundY` given — the dust the blast
   * kicks up off the ground under the muzzle. The signature of the ground war.
   */
  tankBlast(pos, dir, groundY = null) {
    if (!this.soft) return this._blockTankBlast(pos, dir, groundY);
    const S = this.sprites;
    const side = FX_SHEETS.muzzle.sideOn;
    S.spawn("muzzle", pos.x, pos.y, pos.z, {
      frame: side[(Math.random() * side.length) | 0], size: 6.5, stretch: 1.3, dir: [dir.x, dir.y, dir.z],
      life: 0.1, fadeOut: 0.05, emit: 2.2, push: 0.1, bias: -2,
    });
    // A quick orange bloom off the muzzle (the flipbook's burning middle,
    // played fast): the lance is the flash.
    S.spawn("explosion", pos.x + dir.x * 1.5, pos.y + dir.y * 1.5, pos.z + dir.z * 1.5, {
      size: 4.2, grow: 0.5, frame: 11, end: 22, speed: 2.2,
      vx: dir.x * 14, vy: dir.y * 14, vz: dir.z * 14, drag: 3,
      mirror: coin(), roll: rnd(0.4), fadeOut: 0.08, emit: 1.2, push: 0.3, bias: -1,
    });
    // A puff of cordite blown out along the bore and thinning within a
    // couple of seconds: a tank gun speaks, it doesn't hang a cloud.
    for (let i = 0; i < 2; i++) {
      const a = 1.5 + i * 2, v = 6 + i * 4;
      S.spawn("smoke", pos.x + dir.x * a, pos.y + dir.y * a, pos.z + dir.z * a, {
        size: 3.4 + i, grow: 1.3, speed: 0.85, frame: 14, alpha: 0.7, fadeIn: 0.05, fadeOut: 0.6, tint: CORDITE,
        vx: dir.x * v, vy: dir.y * v + 0.8, vz: dir.z * v, drag: 1.1, rise: 0.6, wind: 1,
        mirror: coin(), roll: rnd(0.4), push: 0.3, bias: 1,
      });
    }
    if (groundY != null) {
      S.spawn("dust", pos.x + dir.x * 3, groundY, pos.z + dir.z * 3, {
        size: 10, grow: 0.4, speed: 1.1, alpha: 0.7, tint: DUST_TAN, mirror: coin(),
        wind: 1, rise: 0.3, fadeOut: 0.6, push: 0.4, bias: 2,
      });
    }
  }

  // A single drifting vapor puff, dropped repeatedly along a damaged plane's
  // path: fuel venting. `vel` (the airframe's) keeps the trail close behind it.
  vapor(pos, { vel = null } = {}) {
    if (!this.soft) return this._blockVapor(pos);
    this._puff(pos, { size: 2.2, grow: 1.4, speed: 1.2, alpha: 0.42, tint: WHITE, rise: 0.4, vel });
  }

  /**
   * A flame licking up off a burning wreck, crater, hull or airframe; called
   * a few times a second, the flames overlap into a steady burn. `size` is
   * the flame's frame (m); `vel` a moving burner's velocity.
   *
   * A flame stands on the wind it burns in: straight up off a standing fire,
   * swept back off a moving one. A fighter's burning engine streams its fire
   * behind it like a torch, not a row of campfires down the sky. `airflow`
   * (m/s) is that wind when there's no velocity to read it from: a staged
   * plane that only poses as flying.
   */
  fire(pos, { size = 3.2, vel = null, airflow = null } = {}) {
    if (!this.soft) return this._blockFire(pos);
    const a = airflow ?? (vel ? { x: -vel.x, y: -vel.y, z: -vel.z } : null);
    // How fast the air goes by, as a share of a fighter's cruise: 0 burns
    // upright, 1 streams flat out.
    let k = 0, stand = null;
    if (a) {
      const ay = a.y + FLAME_LIFT;
      const l = Math.hypot(a.x, ay, a.z);
      k = Math.min(1, Math.hypot(a.x, a.y, a.z) / 80);
      stand = [a.x / l, ay / l, a.z / l];
    }
    // At speed the torch is drawn out long and lean, burns shorter, and keeps
    // nearly all the airframe's speed: it hangs off the burner, the air
    // bleeding it back a few metres before it gutters out. It burns warmer,
    // too — the sheet's white-hot base, laid on its side and overlapping
    // itself, otherwise washes the torch out to a pale blob.
    const keep = 0.85 + 0.13 * k;
    this.sprites.spawn("fire", pos.x + rnd(0.3 * (1 - k)), pos.y, pos.z + rnd(0.3 * (1 - k)), {
      size: size * (1 + 1.2 * k) * (0.8 + Math.random() * 0.4), stretch: 1 - 0.45 * k, stand,
      frame: (Math.random() * 64) | 0, life: 0.85 - 0.4 * k,
      fadeIn: 0.12 - 0.06 * k, fadeOut: 0.35 - 0.05 * k, rise: 1.4 * (1 - k), wind: 0.3,
      emit: 1.3 - 0.15 * k, tint: k > 0 ? [1, 1 - 0.24 * k, 1 - 0.5 * k] : undefined,
      mirror: coin(), roll: rnd(0.12), push: 0.2, bias: -0.5,
      vx: vel ? vel.x * keep : 0, vy: vel ? vel.y * keep : 0, vz: vel ? vel.z * keep : 0, drag: vel ? 2 - k : 0,
    });
  }

  /**
   * Smoke off a fire or a damaged engine: a dark puff that billows as it
   * climbs and drifts downwind. `size`, `tint` and `vel` as for fire.
   */
  smoke(pos, { size = 3.6, tint = SMOKE_BLACK, vel = null, alpha = 0.8 } = {}) {
    if (!this.soft) return this._blockSmoke(pos);
    // Off a standing fire the heat lifts it: puffs laid a few times a second
    // climb into one column. Off a moving airframe it trails instead.
    this._puff(pos, {
      size: size * (0.8 + Math.random() * 0.4), grow: 1.6, speed: vel ? 1.2 : 0.8, alpha, tint, rise: vel ? 1.5 : 3.5,
      vel, lift: vel ? 0 : 2.2, drag: vel ? null : 0.45, spin: 0.15,
    });
  }

  /**
   * Steam and spray boiling off a hull going down, `k` its size (1: a
   * cruiser's waterline).
   */
  steam(pos, k = 1) {
    if (!this.soft) {
      this.spawn(pos, {
        count: 10, speed: 6, life: 2.2, size: 3.0 * k,
        colors: [0xeef2f6, 0xd8dde2, 0xc4cace, 0xb6bcc2],
        gravity: -5, drag: 0.9, spin: 0.3, grow: true,
      });
      return;
    }
    for (let i = 0; i < 2; i++) {
      this.sprites.spawn("smoke", pos.x + rnd(3 * k), pos.y + i * 2 * k, pos.z + rnd(3 * k), {
        size: 14 * k, grow: 1.2, speed: 0.5, frame: 2, alpha: 0.75, fadeIn: 0.2, tint: WHITE,
        vx: rnd(2), vy: 3, vz: rnd(2), drag: 0.6, rise: 3.5, wind: 1,
        mirror: coin(), roll: rnd(0.4), push: 0.3,
      });
    }
  }

  /**
   * A soft puff of smoke — a funnel's plume, a skywriter's dot, a canopy
   * popping — with `block` the cube spawn it throws instead when the
   * flipbooks aren't drawing (omit it for nothing at all).
   */
  puff(pos, o = {}, block = null) {
    if (!this.soft) {
      if (block) this.spawn(pos, block);
      return;
    }
    this._puff(pos, o);
  }

  // One soft puff: `vel` a moving emitter's velocity (the puff starts with
  // most of it, so a trail streams close behind), `lift` an updraft (m/s).
  _puff(pos, { size = 3, grow = 1.2, speed = 0.8, alpha = 0.7, tint = WHITE, rise = 1, wind = 1, vel = null, lift = 0, drag = null, spin = 0 } = {}) {
    this.sprites.spawn("smoke", pos.x, pos.y, pos.z, {
      size, grow, speed, alpha, fadeIn: 0.1, tint, rise, wind,
      vx: vel ? vel.x * 0.85 : 0, vy: (vel ? vel.y * 0.85 : 0) + lift, vz: vel ? vel.z * 0.85 : 0,
      drag: drag ?? (vel ? 1.6 : 0.4),
      mirror: coin(), roll: rnd(0.4), spin: rnd(spin), push: 0.3, bias: 0.5,
    });
  }

  /** A spout of spray (a whale blowing): a little water column. */
  spray(pos, size = 6, block = null) {
    if (!this.soft) {
      if (block) this.spawn(pos, block);
      return;
    }
    this.sprites.spawn("splash", pos.x, pos.y, pos.z, { size, speed: 1.3, frame: 4, mirror: coin(), alpha: 0.85, tint: SPRAY, push: 0.1 });
  }

  // --- the block recipes: the world's original particles, which stand in
  // whole until the flipbooks land (and under ?boxes) -------------------------

  _blockExplosion(pos) {
    this.spawn(pos, {
      count: 38,
      speed: 42,
      life: 1.4,
      size: 2.6,
      colors: [0xff7b2e, 0xffc14d, 0x3a3a3a, 0x222222, 0xff4d2e],
    });
  }

  // Bomb detonation: a bigger fireball plus a column of thrown-up debris.
  _blockBombBlast(pos) {
    this.spawn(pos, {
      count: 50,
      speed: 55,
      life: 1.7,
      size: 3.2,
      colors: [0xff7b2e, 0xffc14d, 0x3a3a3a, 0x222222, 0xff4d2e],
    });
    this.spawn(pos, {
      count: 20,
      speed: 38,
      life: 2.2,
      size: 2.4,
      colors: [0x5a4a32, 0x6b5b40, 0x44382a],
      gravity: 22, // dirt goes up fast and rains back down
      drag: 0.25,
    });
  }

  // Main-battery shell impact: a taller fireball and a heavier debris column
  // than a bomb — naval rifles announce themselves.
  _blockShellBlast(pos) {
    this.spawn(pos, {
      count: 70,
      speed: 70,
      life: 2.0,
      size: 3.8,
      colors: [0xff7b2e, 0xffc14d, 0x3a3a3a, 0x222222, 0xff4d2e],
    });
    this.spawn(pos, {
      count: 30,
      speed: 48,
      life: 2.6,
      size: 2.8,
      colors: [0x5a4a32, 0x6b5b40, 0x44382a],
      gravity: 24,
      drag: 0.22,
    });
  }

  // Naval rifle firing: a hard white-gold muzzle flash thrown out along the
  // bore, wrapped in a rolling cloud of brown-grey gunsmoke that hangs and
  // rises off the barrels.
  _blockMuzzleBlast(pos, dir, k = 1) {
    const n = (c) => Math.max(3, Math.round(c * k)); // particle counts
    const s = 0.55 + 0.45 * k; // sizes: even a small gun keeps some presence
    // Smoke lifts up off the bore (buoyancy + a little downrange push), not
    // straight downrange.
    const smokeDir = new THREE.Vector3(0, 1, 0).addScaledVector(dir, 0.5).normalize();
    // The fireball sits ahead of the muzzle (as on a real broadside), which
    // also keeps it clear of the smoke rising at the bore so it reads in any
    // light.
    const tip = pos.clone().addScaledVector(dir, 3.5 * s);
    // Fireball body: lit (opaque) so it has presence in daylight.
    this.spawn(tip, {
      count: n(44), speed: 38 * s, life: 0.85, size: 8.2 * s,
      colors: [0xffc14d, 0xffb347, 0xff9a3d, 0xffd76b, 0xff8a2e],
      gravity: 0, drag: 0.74, dir, spread: 0.5, grow: true,
    });
    // Fireball bloom: an additive glow over the body so it flares at dusk and
    // night, where the lit cubes would go dark.
    this.spawn(tip, {
      count: n(34), speed: 30 * s, life: 0.75, size: 9.0 * s,
      colors: [0xffb347, 0xff9a3d, 0xffc14d, 0xffa838],
      gravity: 0, drag: 0.82, dir, spread: 0.55, grow: true, glow: true,
    });
    // White-hot core: a small, brief, intense flash at the muzzle.
    this.spawn(pos, {
      count: n(8), speed: 12 * s, life: 0.3, size: 6.5 * s,
      colors: [0xffffff, 0xfff0c0, 0xffe9a8],
      gravity: 0, drag: 1.0, dir, spread: 0.6, grow: true, glow: true,
    });
    // Burning amber edges and a lance of sparks thrown downrange.
    this.spawn(tip, {
      count: n(26), speed: 100 * s, life: 0.78, size: 4.4 * s,
      colors: [0xff8a2e, 0xffae4d, 0xff6a1e, 0xffd76b],
      gravity: 0, drag: 0.5, dir, spread: 0.26, grow: true, glow: true,
    });
    // Gunsmoke: a dense, slow, buoyant plume that billows up and disperses.
    this.spawn(pos, {
      count: n(44), speed: 8 * s, life: 3.2, size: 4.2 * s,
      colors: [0xc4c4c4, 0xd2ccc0, 0xa8a298, 0xe0d9cc, 0x8f897e],
      gravity: -7, drag: 0.92, dir: smokeDir, spread: 0.45, spin: 0.5, grow: true,
    });
    // A low cordite ring hugging the muzzle as the column lifts.
    this.spawn(pos, {
      count: n(16), speed: 22 * s, life: 1.1, size: 3.0 * s,
      colors: [0xcfc9bd, 0xe2dbcd, 0xada79b],
      gravity: -3, drag: 0.9, dir, spread: 0.6, spin: 0.6, grow: true,
    });
  }

  // A tank's report: a tank-scale flash at the muzzle, a puff of cordite and
  // a low dust ring off the ground under it.
  _blockTankBlast(pos, dir, groundY) {
    this.spawn(pos, {
      count: 5, speed: 16, life: 0.3, size: 0.9,
      colors: [0xffd76b, 0xffb347, 0xfff2c8], gravity: 0, drag: 3,
    });
    this.spawn(new THREE.Vector3(pos.x + dir.x * 3, pos.y, pos.z + dir.z * 3), {
      count: 3, speed: 5, life: 0.8, size: 1.0,
      colors: [0xcfc9bd, 0xbdb6a8], gravity: -1, drag: 2.4,
    });
    if (groundY != null) {
      this.spawn(new THREE.Vector3(pos.x, groundY + 0.3, pos.z), {
        count: 5, speed: 6, life: 1.1, size: 1.1,
        colors: [0xbfae8a, 0xa89a78], gravity: -1.2, drag: 2.4,
      });
    }
  }

  _blockVapor(pos) {
    this.spawn(pos, {
      count: 1, speed: 1.8, life: 1.7, size: 2.3,
      colors: [0xeef2f6, 0xd8dde2, 0xc4cace, 0xb6bcc2],
      gravity: -0.8, drag: 1.3, spin: 0.25, grow: true,
    });
  }

  // Shell short into the sea: a tall white water column.
  _blockShellSplash(pos, { big = false, heavy = false } = {}) {
    if (big || heavy) {
      // Naval fall-of-shot: legible at the 3-8 km where you walk main-battery
      // fire onto a target — and a super-heavy's climbs higher and wider.
      const k = heavy ? 1.3 : 1;
      this.spawn(pos, {
        count: Math.round(90 * k),
        speed: 70 * k,
        life: 2.6 * k,
        size: 4.6 * k,
        colors: [0x9fd4f5, 0xd5ecfa, 0x4a90c9, 0xeef7fc],
        gravity: 16,
        drag: 0.28,
      });
      // A slim white core spike so it reads as a rising column, not a puff.
      this.spawn(pos, {
        count: Math.round(18 * k),
        speed: 85 * k,
        life: 2.2 * k,
        size: 3 * k,
        colors: [0xffffff, 0xeef7fc],
        gravity: 20,
        drag: 0.2,
        spread: 0.12,
        dir: new THREE.Vector3(0, 1, 0),
      });
      return;
    }
    this.spawn(pos, {
      count: 46,
      speed: 44,
      life: 1.6,
      size: 2.6,
      colors: [0x9fd4f5, 0xd5ecfa, 0x4a90c9, 0xeef7fc],
      gravity: 16,
      drag: 0.3,
    });
  }

  _blockHitSpark(pos) {
    // A round cracking off steel: a hot WHITE flash with a spray of tiny
    // sparks. The brightness trick is overlap: a tight, slow-moving KNOT of
    // additive cubes lands on the same pixels and stacks past white-hot.
    // Solid white-hot core: opaque, scene-lit, PURE white.
    this.spawn(pos, {
      count: 5,
      speed: 6,
      life: 0.12,
      size: 0.32,
      colors: [0xffffff],
      gravity: 0,
      drag: 2.2,
    });
    // Additive bloom over that core: makes it flare white-hot at dusk and night.
    this.spawn(pos, {
      count: 7,
      speed: 4,
      life: 0.14,
      size: 0.34,
      colors: [0xffffff, 0xfff6e6],
      gravity: 0,
      drag: 2.4,
      glow: true,
    });
    // Spark spray: small, fast, white-hot pinpoints streaking outward.
    this.spawn(pos, {
      count: 10,
      speed: 54,
      life: 0.26,
      size: 0.15,
      colors: [0xffffff, 0xffffff, 0xfff4d6, 0xffcf7a],
      gravity: 8,
      drag: 0.32,
      glow: true,
    });
    // A couple of amber embers with a little hang, scene-lit, for body.
    this.spawn(pos, {
      count: 2,
      speed: 22,
      life: 0.36,
      size: 0.22,
      colors: [0xffc978, 0xffaa55],
      gravity: 10,
      drag: 0.5,
    });
  }

  // A gun round into the water, in blocks: a thin jet of spray straight up.
  _blockGunSplash(pos, size) {
    const k = size / 4.4;
    this.spawn(pos, {
      count: Math.round(4 + 3 * k),
      speed: 11 * k,
      life: 0.7,
      size: 0.4 * k,
      colors: [0xd5ecfa, 0xeef7fc, 0x9fd4f5],
      gravity: 16,
      drag: 0.4,
      spread: 0.2,
      dir: UP,
    });
  }

  _blockSplash(pos) {
    this.spawn(pos, {
      count: 22,
      speed: 30,
      life: 1.0,
      size: 1.8,
      colors: [0x9fd4f5, 0xd5ecfa, 0x4a90c9],
    });
  }

  _blockFire(pos) {
    this.spawn(pos, {
      count: 2,
      speed: 5,
      life: 0.55,
      size: 1.3,
      colors: [0xff7b2e, 0xffae3d, 0xff4d2e, 0x2e2e2e],
      gravity: -8, // flames lick upward
      drag: 1.2,
    });
  }

  _blockSmoke(pos) {
    this.spawn(pos, {
      count: 1,
      speed: 3,
      life: 1.6,
      size: 1.7,
      colors: [0x4d4d4d, 0x6b6b6b, 0x303030],
      gravity: -3, // smoke rises
      drag: 1.4,
    });
  }

  /** Step the blocks and the sparks by dt; `eye` sorts the sparks' light. */
  update(dt, eye = null) {
    this._wet = Math.min(WET_BURST, this._wet + WET_PER_SEC * dt);
    this.sparks.update(dt, eye);
    const p = this.parts;
    // Lift every field into a local before the loops — see the note on
    // SoAPool. Indexing `p.f.px[k]` per particle per field is a property load
    // each time and dominates the frame at pool capacity.
    const { px, py, pz, vx, vy, vz, life, maxLife, size, gravity, drag, spin } = p.f;
    const flagsArr = p.b.flags;
    const colorArr = p.u.color;

    // Step and compact in one pass, in place: survivors slide down over the
    // dead and keep their spawn order (the blended glow layer is drawn in
    // submission order, so shuffling them would shuffle the fire).
    let live = 0;
    for (let k = 0; k < p.count; k++) {
      const l = life[k] - dt;
      if (l <= 0) continue;
      if (live !== k) p.copySlot(k, live);
      life[live] = l;
      const damp = Math.max(0, 1 - drag[live] * dt);
      const nvx = vx[live] * damp;
      const nvy = (vy[live] - gravity[live] * dt) * damp;
      const nvz = vz[live] * damp;
      vx[live] = nvx; vy[live] = nvy; vz[live] = nvz;
      px[live] += nvx * dt; py[live] += nvy * dt; pz[live] += nvz * dt;
      live++;
    }
    p.count = live;

    const dummy = this.dummy;
    let litN = 0;
    let glowN = 0;
    for (let k = 0; k < live; k++) {
      const l = life[k], sp = spin[k];
      const t = l / maxLife[k];
      dummy.position.set(px[k], py[k], pz[k]);
      dummy.rotation.set(sp * l, sp, 0);
      // Debris shrinks as it dies; smoke/vapor/fire (grow) billows outward as
      // it ages and then thins away over its last quarter, so a dense spawn
      // reads as a rising, dispersing cloud rather than a pop of blocks.
      const flags = flagsArr[k];
      let s;
      if (flags & 1) {
        const age = 1 - t;
        const tail = Math.min(1, t / 0.28);
        s = size[k] * (0.5 + age * 0.95) * tail;
      } else {
        s = size[k] * (0.4 + 0.6 * t);
      }
      dummy.scale.set(s, s, s);
      dummy.updateMatrix();
      if (flags & 2) {
        if (glowN >= this.glowCap) continue;
        this.glowMesh.setMatrixAt(glowN, dummy.matrix);
        this.glowMesh.setColorAt(glowN, this.color.setHex(colorArr[k]));
        glowN++;
      } else {
        if (litN >= this.capacity) continue;
        this.mesh.setMatrixAt(litN, dummy.matrix);
        this.mesh.setColorAt(litN, this.color.setHex(colorArr[k]));
        litN++;
      }
    }
    uploadInstances(this.mesh, litN, true);
    uploadInstances(this.glowMesh, glowN, true);
  }
}
